/**
 * Order planning (2026-08-24 design doc:
 * docs/plans/2026-08-24-order-planning-design.md). React-query hooks for
 * GET /api/order-planning/recommendations and the plans/lines/research CRUD,
 * plus the pure verdict/recommendation badge mappings, money/percent
 * formatting, and the sparkline path builder used by PlanningPage.
 *
 * Invalidation convention (per the task brief): any line or plan mutation
 * invalidates both the plan detail (['order-plan', planId]) and the plans
 * list (['order-plans']); research invalidates the plan detail only (the
 * list view never shows research/verdict data).
 */
import { useEffect, useState } from 'react';
import type { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BuyingSettings,
  UpdateBuyingSettingsRequest,
  ListOrderPlanRecommendationsResponse,
  ListOrderPlansResponse,
  OrderPlan,
  OrderPlanLine,
  CreateOrderPlanResponse,
  CreateOrderPlanLineResponse,
  ResearchOrderPlanLineResponse,
  SetReorderExclusionResponse,
  PutPackOverrideResponse,
  PutFeeOverrideResponse,
  DeleteFeeOverrideResponse,
  DeletePackOverrideResponse,
  PutSellableOverrideResponse,
  DeleteSellableOverrideResponse,
  ProductSearchResponse,
  OrderPlanKeepaBudgetResponse,
  LatestPlanningRunResponse,
  ListPlanningCandidatesResponse,
  ListPlanningResearchResponse,
  OrderImportResponse,
  OrderImportCandidateSummary,
  BulkResearchPlanResponse,
  type OrderImportPreviewResponse,
  type OrderImportRequest,
  type PlanningRun,
  type PlanningCandidate,
  type PlanningGatingStatus,
  type PlanningGateKind,
  type PlanningResearchPack,
  type PlanningResearchDiscount,
  type PlanningVelocityTier,
  type PlanningResearchVelocity,
  type PlanningVerdictBucket,
  type PlanningVerdictReason,
  type PlanningResearchVerdictCounts,
  type PlanningResearchAnchorSource,
  type PlanningResearchAnchorSpread,
  type CreateOrderPlanRequest,
  type CreateOrderPlanLineRequest,
  type UpdateOrderPlanRequest,
  type UpdateOrderPlanLineRequest,
  type OrderPlanStatus,
  type OrderPlanLineVerdict,
  type OrderPlanLineVerdictTier,
  type OrderPlanLineAnchorSource,
  type OrderPlanLineResearch,
  type OrderPlanLineResearchStep,
  type AmazonAuthorizationStatus,
  type ReorderRecommendation,
  type SetReorderExclusionRequest,
  type ProductSearchResult,
  type PutPackOverrideRequest,
  type PutFeeOverrideRequest,
  type PutSellableOverrideRequest,
  type PlanningResearchRow,
  type PlanningResearchSellableOverride,
} from '@sourcito/schemas';
import { apiFetch, ApiCallError } from './api';
import { shortDateLabel } from './shipments';
import { useModuleEnabled } from './auth';

// ─────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────

export function fetchBuyingSettings() {
  return apiFetch('/api/order-planning/settings', { responseSchema: BuyingSettings });
}

type BuyingSettingsUpdate = z.infer<typeof UpdateBuyingSettingsRequest>;

export async function updateBuyingSettings(input: BuyingSettingsUpdate) {
  return apiFetch('/api/order-planning/settings', {
    method: 'PUT',
    body: JSON.stringify(UpdateBuyingSettingsRequest.parse(input)),
    responseSchema: BuyingSettings,
  });
}

export function useBuyingSettings() {
  return useQuery({ queryKey: ['buying-settings'], queryFn: fetchBuyingSettings });
}

export function useUpdateBuyingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateBuyingSettings,
    onSuccess: (settings) => {
      qc.setQueryData(['buying-settings'], settings);
      return qc.invalidateQueries({ queryKey: ['buying-settings'] });
    },
  });
}

// This parser only ever handles the money/percent-style fields the buying
// settings editor exposes as text inputs — every BuyingSettings key except
// sourcingRankKey, which is the one non-numeric setting (added 2026-09-06
// for the sourcing screen) and has no UI here yet.
type NumericBuyingSettingKey = Exclude<keyof BuyingSettings, 'sourcingRankKey'>;

export function parseBuyingSettingsInputs(
  inputs: Partial<Record<NumericBuyingSettingKey, string>>,
  persisted: BuyingSettings
): BuyingSettingsUpdate {
  const values: BuyingSettingsUpdate = {};
  for (const key of Object.keys(inputs) as NumericBuyingSettingKey[]) {
    const raw = inputs[key]!.trim();
    const isPercent = key === 'expectedFillRate';
    if (!(isPercent ? /^\d+(?:\.\d+)?$/ : /^\d+(?:\.\d{1,2})?$/).test(raw)) {
      throw new Error(
        isPercent
          ? 'Enter a valid fill percentage.'
          : 'Enter a dollar amount with at most two decimal places.'
      );
    }
    const value = isPercent ? Number(raw) / 100 : Math.round(Number(raw) * 100);
    if (!Number.isFinite(value) || (!isPercent && !Number.isSafeInteger(value))) {
      throw new Error('Enter an amount within the supported range.');
    }
    values[key] = value;
  }
  const update = UpdateBuyingSettingsRequest.parse(values);
  BuyingSettings.parse({ ...persisted, ...update });
  return update;
}

export function useOrderPlanRecommendations(enabled = true) {
  return useQuery({
    queryKey: ['order-plan-recommendations'] as const,
    enabled,
    queryFn: () =>
      apiFetch('/api/order-planning/recommendations', {
        responseSchema: ListOrderPlanRecommendationsResponse,
      }),
  });
}

export function fetchLatestPlanningRun() {
  return apiFetch('/api/order-planning/runs/latest', {
    responseSchema: LatestPlanningRunResponse,
  });
}

// Research page (Faraz 2026-09-28 design): while the newest run is still
// researching, the page falls back to the last COMPLETE run so it isn't
// stuck showing a near-empty in-progress one. Same response shape as
// fetchLatestPlanningRun, filtered server-side to status='complete'.
export function fetchLatestCompletePlanningRun() {
  return apiFetch('/api/order-planning/runs/latest?status=complete', {
    responseSchema: LatestPlanningRunResponse,
  });
}

export async function startPlanningRunNow() {
  return apiFetch('/api/order-planning/runs/run-now', { method: 'POST' });
}

export function useStartPlanningRunNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startPlanningRunNow,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning-run-latest'] }),
  });
}

// Sellability check (Faraz 2026-09-26 fix for the conditionType gating bug:
// see apps/api/src/lib/adapters/sp-api.ts's getListingsRestrictions). POSTs
// the same 'order-planning-gating' kickoff the run's own automatic publish
// path already sends (routes/planning-runs.ts's /:runId/gating-check) —
// `recheck: true` re-checks every row, including ones already stamped
// (wrongly) 'blocked' before the fix.
export function requestGatingCheck(
  runId: string,
  { recheck }: { recheck: boolean } = {
    recheck: false,
  }
) {
  return apiFetch(`/api/order-planning/runs/${runId}/gating-check?recheck=${recheck ? '1' : '0'}`, {
    method: 'POST',
  });
}

export function useRequestGatingCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, recheck }: { runId: string; recheck: boolean }) =>
      requestGatingCheck(runId, { recheck }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['planning-run-research'], exact: false }),
  });
}

export function useLatestPlanningRun(enabled = true) {
  return useQuery({
    queryKey: ['planning-run-latest'] as const,
    enabled,
    queryFn: fetchLatestPlanningRun,
    refetchInterval: (query) =>
      query.state.data?.status === 'pending' ||
      query.state.data?.status === 'researching' ||
      query.state.data?.status === 'publishing'
        ? 15_000
        : false,
  });
}

export function useLatestCompletePlanningRun(enabled = true) {
  return useQuery({
    queryKey: ['planning-run-latest-complete'] as const,
    enabled,
    queryFn: fetchLatestCompletePlanningRun,
  });
}

export type PlanningCandidateFilters = {
  limit?: number;
  offset?: number;
  status?: PlanningCandidate['status'];
  source?: PlanningCandidate['source'];
};

export function fetchPlanningCandidates(runId: string, filters: PlanningCandidateFilters = {}) {
  const params = new URLSearchParams({
    limit: String(filters.limit ?? 50),
    offset: String(filters.offset ?? 0),
  });
  if (filters.status) params.set('status', filters.status);
  if (filters.source) params.set('source', filters.source);
  return apiFetch(`/api/order-planning/runs/${runId}/candidates?${params.toString()}`, {
    responseSchema: ListPlanningCandidatesResponse,
  });
}

export function usePlanningCandidates(
  runId: string | null,
  filters: PlanningCandidateFilters = {},
  enabled = true
) {
  return useQuery({
    queryKey: ['planning-run-candidates', runId, filters] as const,
    enabled: enabled && runId != null,
    queryFn: () => fetchPlanningCandidates(runId!, filters),
  });
}

/**
 * GET /:runId/research (Faraz 2026-09-06: "show this table, I'll do order
 * planning later" — the procurement research page; simplified to one
 * verdict per row 2026-09-11: "we have a list ... find the products", see
 * docs/plans/2026-09-11-research-verdict-simplification-design.md). `verdict`
 * and `reason` are 1:1 query params (no comma-joining — the API only ever
 * takes one of each); `researchQueryParams` is split out (pure, no fetch) so
 * the request shape is unit-testable without mocking apiFetch.
 */
export interface PlanningResearchFilters {
  /** Which of the three tabs (Buy/Fix first/Everything) is active —
   *  undefined ('Everything') sends no `verdict` param, every researched
   *  row regardless of bucket. */
  verdict?: PlanningVerdictBucket;
  /** The Reason select's choice — only meaningful (and only rendered in the
   *  UI) alongside `verdict: 'fix'`; undefined sends no `reason` param. */
  reason?: PlanningVerdictReason;
  /** "Split the list" (Faraz 2026-09-26) — the Buy tab's own segmented
   *  control ("Buy now" / "After approval") drives this directly; undefined
   *  sends no `gating` param. Comma-joined, mirroring the API's multi-select
   *  shape ("After approval" sends `approval_required,unchecked`). */
  gating?: PlanningGatingStatus[];
  velocity?: PlanningVelocityTier[];
  /** Pack filter (2026-09-07 pack-size design). Re-exposed 2026-09-22 after
   *  Faraz: "We dont have any place in UI to fix these issues." The control
   *  was dropped when nine filter selects were consolidated into three tabs
   *  plus a reason select, but the route, the projection reader and the
   *  `counts.pack` totals all kept working — only the way to ASK for these
   *  rows was missing. 'suspect' is the one that matters: a cost basis we do
   *  not trust, which the row panel's pack override exists to correct. */
  pack?: 'known' | 'unclear' | 'unverified' | 'suspect';
  q?: string;
  sort?:
    | 'margin'
    | 'marginPct'
    | 'price'
    | 'rank'
    | 'cost'
    | 'fees'
    | 'offers'
    | 'sales'
    | 'title'
    // Combined "Profit/mo" sort (Faraz 2026-09-27: "top selling top margin
    // products on top") — margin x sales, computed server-side. See
    // sortColumns in research-projection-reader.ts.
    | 'profit';
  /** Sort direction (Faraz 2026-09-10: "I need a way to sort research list
   *  in asc/desc order"). Defaults to 'desc', matching the route's
   *  pre-existing (previously hardcoded) behavior. */
  dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export function researchQueryParams(filters: PlanningResearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.verdict) params.set('verdict', filters.verdict);
  if (filters.reason) params.set('reason', filters.reason);
  if (filters.gating?.length) params.set('gating', filters.gating.join(','));
  if (filters.velocity?.length) params.set('velocity', filters.velocity.join(','));
  if (filters.pack) params.set('pack', filters.pack);
  if (filters.q) params.set('q', filters.q);
  params.set('sort', filters.sort ?? 'margin');
  params.set('dir', filters.dir ?? 'desc');
  params.set('limit', String(filters.limit ?? 100));
  params.set('offset', String(filters.offset ?? 0));
  return params;
}

export function fetchPlanningResearch(runId: string, filters: PlanningResearchFilters = {}) {
  return apiFetch(`/api/order-planning/runs/${runId}/research?${researchQueryParams(filters)}`, {
    responseSchema: ListPlanningResearchResponse,
  });
}

// 2026-09-17 provider-queues Task 6: poll while there's genuinely more to
// see, same convention as useLatestPlanningRun's own refetchInterval above
// — 'preparing'/'refreshing' freshness (pre-existing) or a providerProgress
// runState short of a terminal state (new). A terminal state (complete /
// complete_with_gaps) or the absence of providerProgress (a legacy run,
// which never changes on its own outside a manual "Refresh results") stops
// polling. Query state, not component state — filters/pagination/selection
// are untouched by a poll's refetch.
export function planningResearchIsActive(data: ListPlanningResearchResponse | undefined): boolean {
  if (!data) return false;
  if (data.freshness?.status === 'preparing' || data.freshness?.status === 'refreshing')
    return true;
  const runState = data.providerProgress?.runState;
  return runState !== undefined && runState !== 'complete' && runState !== 'complete_with_gaps';
}

export function usePlanningResearch(
  runId: string | null,
  filters: PlanningResearchFilters = {},
  enabled = true
) {
  return useQuery({
    queryKey: ['planning-run-research', runId, filters] as const,
    enabled: enabled && runId != null,
    queryFn: () => fetchPlanningResearch(runId!, filters),
    refetchInterval: (query) => (planningResearchIsActive(query.state.data) ? 20_000 : false),
  });
}

export function useOrderPlans(status?: OrderPlanStatus, enabled = true) {
  return useQuery({
    queryKey: ['order-plans', status ?? 'all'] as const,
    enabled,
    queryFn: () =>
      apiFetch(`/api/order-planning/plans${status ? `?status=${status}` : ''}`, {
        responseSchema: ListOrderPlansResponse,
      }),
  });
}

export function useOrderPlan(id: string | null) {
  return useQuery({
    queryKey: ['order-plan', id] as const,
    enabled: id != null,
    queryFn: () => apiFetch(`/api/order-planning/plans/${id}`, { responseSchema: OrderPlan }),
  });
}

/**
 * GET /api/order-planning/product-search?q= (Phase A item 4, planning page
 * usability design 2026-08-31) — AddLineForm's manual-add autocomplete.
 * Debounced ~250ms so a fast typist doesn't fire a request per keystroke;
 * disabled while `q` is empty (nothing to search) or the amazon module is
 * off (the route requires it — a bare disabled fetch would just 403 anyway).
 */
export function useProductSearch(q: string) {
  const amazonEnabled = useModuleEnabled('amazon');
  const [debouncedQ, setDebouncedQ] = useState(q);
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQ(q), 250);
    return () => clearTimeout(handle);
  }, [q]);
  const trimmed = debouncedQ.trim();
  return useQuery({
    queryKey: ['product-search', trimmed] as const,
    enabled: amazonEnabled && trimmed.length > 0,
    queryFn: () =>
      apiFetch(`/api/order-planning/product-search?q=${encodeURIComponent(trimmed)}`, {
        responseSchema: ProductSearchResponse,
      }),
  });
}

/**
 * GET /api/order-planning/keepa-budget (Phase A.1, 2026-08-31
 * planning-page-usability design, "Keepa token honesty") — a no-spend
 * readout of the platform Keepa key's estimated bucket level, for the
 * "~N Keepa tokens" meter and the disabled-countdown state near "Check
 * margin", shown even before the first research click on a plan.
 */
export function useOrderPlanKeepaBudget() {
  return useQuery({
    queryKey: ['order-plan-keepa-budget'] as const,
    queryFn: () =>
      apiFetch('/api/order-planning/keepa-budget', {
        responseSchema: OrderPlanKeepaBudgetResponse,
      }),
  });
}

// ─────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────

export function useCreateOrderPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderPlanRequest = {}) =>
      apiFetch('/api/order-planning/plans', {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateOrderPlanResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plans'] });
    },
  });
}

export function useUpdateOrderPlan(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrderPlanRequest) =>
      apiFetch(`/api/order-planning/plans/${planId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: OrderPlan,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
    },
  });
}

/**
 * Plain (non-hook) line creation for callers that don't know the target
 * plan id until the moment of the call (Reorder table's "Add to plan" may
 * need to create the draft plan first) — see PlanningPage's
 * `ensureDraftPlanId`. Callers are responsible for invalidating
 * ['order-plan', planId] and ['order-plans'] afterward.
 */
export function createOrderPlanLine(planId: string, input: CreateOrderPlanLineRequest) {
  return apiFetch(`/api/order-planning/plans/${planId}/lines`, {
    method: 'POST',
    body: JSON.stringify(input),
    responseSchema: CreateOrderPlanLineResponse,
  });
}

export function useCreateOrderPlanLine(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrderPlanLineRequest) =>
      apiFetch(`/api/order-planning/plans/${planId}/lines`, {
        method: 'POST',
        body: JSON.stringify(input),
        responseSchema: CreateOrderPlanLineResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
    },
  });
}

export function useUpdateOrderPlanLine(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateOrderPlanLineRequest) =>
      apiFetch(`/api/order-planning/lines/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: OrderPlanLine,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
    },
  });
}

export function useDeleteOrderPlanLine(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/order-planning/lines/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
    },
  });
}

export function useDeleteOrderPlan(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch(`/api/order-planning/plans/${planId}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.setQueriesData<OrderPlan[]>({ queryKey: ['order-plans'] }, (plans) =>
        plans?.filter((plan) => plan.id !== planId)
      );
      qc.removeQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plans'] });
      qc.invalidateQueries({ queryKey: ['planning-run-latest'] });
    },
  });
}

export function useResearchOrderPlanLine(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/order-planning/lines/${id}/research`, {
        method: 'POST',
        responseSchema: ResearchOrderPlanLineResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      // A research check spends Keepa tokens (platform key) — the budget
      // meter is stale the moment this resolves.
      qc.invalidateQueries({ queryKey: ['order-plan-keepa-budget'] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Order import (2026-09-21 design, Tasks 5-8) — upload a supplier order
// file, preview its UPC→ASIN resolution, and (once every line is confirmed,
// Task 9) create/append a plan from it. previewOrderImport/importOrder are
// plain functions, not mutation hooks: OrderImportModal (Task 8) drives its
// own loading/error state around a single preview call — same reasoning as
// createOrderPlanLine above, a caller doesn't always want react-query's
// cache semantics wrapped around a one-shot action.
// ─────────────────────────────────────────────────────────

export interface PreviewOrderImportInput {
  supplierId: string;
  file: File;
}

/**
 * Multipart POST /api/order-planning/plans/import/preview. Bypasses
 * apiFetch for the same reason useUploadPricingSheet (pricing-sheets.ts)
 * does — apiFetch auto-sets Content-Type: application/json, which breaks
 * the boundary header the browser injects for FormData. No runtime zod
 * validation of the response either, matching that same precedent. Writes
 * nothing server-side (see routes/order-planning.ts's createImportPreviewHandler
 * doc) — safe to call repeatedly while the human fixes a file and re-uploads.
 */
export async function previewOrderImport({
  supplierId,
  file,
}: PreviewOrderImportInput): Promise<OrderImportPreviewResponse> {
  const fd = new FormData();
  fd.append('supplierId', supplierId);
  fd.append('file', file, file.name);
  const res = await fetch('/api/order-planning/plans/import/preview', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const text = await res.text();
  const json: unknown = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const message =
      (json as { error?: { message?: string } })?.error?.message ??
      `Import preview failed with status ${res.status}`;
    throw new ApiCallError(res.status, message, json);
  }
  return json as OrderImportPreviewResponse;
}

/**
 * POST /api/order-planning/plans/import — Task 9's "Create plan" gate. Takes
 * the settled, fully-resolved lines from the preview above (every line's
 * UPC already turned into a confirmed ASIN by the resolve step); the route
 * refuses the whole import (400 UNRESOLVED_LINES) unless every line
 * qualifies. Plain function, not a hook — Task 9's resolve-step component
 * owns the submit/loading state around this the same way OrderImportModal
 * owns it around previewOrderImport above.
 */
export function importOrder(body: OrderImportRequest): Promise<OrderImportResponse> {
  return apiFetch('/api/order-planning/plans/import', {
    method: 'POST',
    body: JSON.stringify(body),
    responseSchema: OrderImportResponse,
  });
}

/**
 * GET /api/order-planning/asin-lookup/:asin — Task 9's typo guard. Called
 * by OrderImportResolveTable when a human types an ASIN by hand (blur, or
 * a Check button), to echo back what it is before it binds. Returns null
 * on a 404 (no Amazon catalog listing for this ASIN) — that's a valid,
 * non-exceptional outcome the caller shows as a warning, NOT a failure:
 * the typed ASIN is authoritative regardless of whether this check
 * confirms it (see the component's doc for why). Any other failure
 * (malformed ASIN, network, 5xx) rethrows as ApiCallError for the caller
 * to turn into the same non-blocking warning.
 */
export function lookupAsin(asin: string): Promise<OrderImportCandidateSummary | null> {
  return apiFetch(`/api/order-planning/asin-lookup/${encodeURIComponent(asin)}`, {
    responseSchema: OrderImportCandidateSummary,
  }).catch((err) => {
    if (err instanceof ApiCallError && err.status === 404) return null;
    throw err;
  });
}

/** POST /api/order-planning/plans/:id/research — Task 10's bulk "check
 *  margins" pass over every line in a plan, reusing already-fresh research
 *  instead of paying for a Keepa/SP-API round trip per line (see
 *  BulkResearchPlanResponse's doc in packages/schemas). Same invalidation
 *  as useResearchOrderPlanLine above: a bulk pass spends the same platform
 *  Keepa key, so the budget meter goes stale too. */
export function useBulkResearchPlan(planId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { force?: boolean } = {}) =>
      apiFetch(`/api/order-planning/plans/${planId}/research`, {
        method: 'POST',
        body: JSON.stringify({ force: input.force ?? false }),
        responseSchema: BulkResearchPlanResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan', planId] });
      qc.invalidateQueries({ queryKey: ['order-plan-keepa-budget'] });
    },
  });
}

/** PATCH /api/order-planning/exclusions/:sellerSku — the Reorder table's
 *  "Don't reorder" / "Allow reordering" toggle. Invalidates the
 *  recommendations list only — exclusions don't touch plans/lines. */
export function useSetReorderExclusion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sellerSku, ...input }: { sellerSku: string } & SetReorderExclusionRequest) =>
      apiFetch(`/api/order-planning/exclusions/${encodeURIComponent(sellerSku)}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        responseSchema: SetReorderExclusionResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order-plan-recommendations'] });
    },
  });
}

/** PUT /api/order-planning/pack-overrides/:asin — the Research page's
 *  editable Pack cell (2026-09-08 design) and the Planning table's Pack
 *  field (2026-09-16 pack-quantity design, Faraz: "a text box to enter
 *  right pack quantity and solve it permanently ... once saved, should be
 *  reflect everywhere"). Invalidates every research query AND every
 *  single-plan query (['order-plan', id], any id — Planning read applies
 *  the override server-side, see routes/order-planning.ts's GET /plans/:id)
 *  so both surfaces refetch with the adjusted cost/margin — the override
 *  is applied server-side immediately, not just "starting with the next
 *  run". */
export function usePutPackOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin, ...input }: { asin: string } & PutPackOverrideRequest) =>
      apiFetch(`/api/order-planning/pack-overrides/${encodeURIComponent(asin)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
        responseSchema: PutPackOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

/** DELETE /api/order-planning/pack-overrides/:asin — the Pack cell's clear
 *  ("x") control. Same invalidation as usePutPackOverride above. */
export function useDeletePackOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin }: { asin: string }) =>
      apiFetch(`/api/order-planning/pack-overrides/${encodeURIComponent(asin)}`, {
        method: 'DELETE',
        responseSchema: DeletePackOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

/** PUT /api/order-planning/fee-overrides/:asin (2026-09-30 design) — the
 *  Research row panel's Verified fee box. Same invalidation as
 *  usePutPackOverride above: Research and Planning both apply the override
 *  server-side immediately. */
export function usePutFeeOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin, ...input }: { asin: string } & PutFeeOverrideRequest) =>
      apiFetch(`/api/order-planning/fee-overrides/${encodeURIComponent(asin)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
        responseSchema: PutFeeOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

/** DELETE /api/order-planning/fee-overrides/:asin — the Verified fee box's
 *  Clear button. Same invalidation as usePutFeeOverride above. */
export function useDeleteFeeOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin }: { asin: string }) =>
      apiFetch(`/api/order-planning/fee-overrides/${encodeURIComponent(asin)}`, {
        method: 'DELETE',
        responseSchema: DeleteFeeOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

export type FeeOverrideParseResult =
  | { ok: true; fulfillmentFeeCents: number; referralFeePct: number }
  | { ok: false };

/** Verified fee box input -> wire values: dollars to cents, percent (0 to
 *  50) to a fraction. The fee must be above $0 (server: 1..100000 cents). */
export function parseFeeOverrideInput(
  feeText: string,
  referralPctText: string
): FeeOverrideParseResult {
  if (feeText.trim() === '' || referralPctText.trim() === '') return { ok: false };
  const dollars = Number(feeText.trim());
  const pct = Number(referralPctText.trim());
  if (!Number.isFinite(dollars) || !Number.isFinite(pct)) return { ok: false };
  const cents = Math.round(dollars * 100);
  if (cents < 1 || cents > 100000 || pct < 0 || pct > 50) return { ok: false };
  return { ok: true, fulfillmentFeeCents: cents, referralFeePct: Number((pct / 100).toFixed(4)) };
}

/** PUT /api/order-planning/sellable-overrides/:asin (2026-09-17 design,
 *  Faraz: "I need a way to mark a product sellable in planning or research
 *  page. Once I check manually, I should be able to remove the
 *  restrictions.") — the Research page's Listing-gate set control and the
 *  Planning line Details panel's set control. Same invalidation as
 *  usePutPackOverride above: both surfaces apply the override server-side
 *  immediately. */
export function usePutSellableOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin, ...input }: { asin: string } & PutSellableOverrideRequest) =>
      apiFetch(`/api/order-planning/sellable-overrides/${encodeURIComponent(asin)}`, {
        method: 'PUT',
        body: JSON.stringify(input),
        responseSchema: PutSellableOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

/** DELETE /api/order-planning/sellable-overrides/:asin — the clear
 *  control. Same invalidation as usePutSellableOverride above. */
export function useDeleteSellableOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asin }: { asin: string }) =>
      apiFetch(`/api/order-planning/sellable-overrides/${encodeURIComponent(asin)}`, {
        method: 'DELETE',
        responseSchema: DeleteSellableOverrideResponse,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['planning-run-research'] });
      qc.invalidateQueries({ queryKey: ['order-plan'] });
    },
  });
}

// ─────────────────────────────────────────────────────────
// Manual sellable override chip (2026-09-17 design) — shared by the
// Research page's row panel and the Planning line Details panel, same
// convention as gatingChip/packBadge above.
// ─────────────────────────────────────────────────────────

/** "Sellable — you checked <date>" chip, null when no override is set —
 *  same absent-means-null convention as packBadge/amazonRetailChip above. */
export function sellableOverrideChip(
  override: PlanningResearchSellableOverride | null
): { label: string; title: string | undefined } | null {
  if (!override) return null;
  return {
    label: `Sellable — you checked ${shortDateLabel(override.updatedAt)}`,
    title: override.note ?? undefined,
  };
}

// ─────────────────────────────────────────────────────────
// Pure helpers (unit-tested in order-planning.test.ts)
// ─────────────────────────────────────────────────────────

type BadgeVariant = 'neutral' | 'accent' | 'positive' | 'warning' | 'negative' | 'info';

/** research.verdict → row Badge. `unknown` reads as "Unverified" — the
 *  research check never ran, or ran and couldn't resolve an ASIN/price. */
const VERDICT_BADGE: Record<OrderPlanLineVerdict, { variant: BadgeVariant; label: string }> = {
  ok: { variant: 'positive', label: 'OK' },
  thin: { variant: 'warning', label: 'Thin' },
  underwater: { variant: 'negative', label: 'Underwater' },
  unknown: { variant: 'neutral', label: 'Unverified' },
};

export function verdictBadge(verdict: OrderPlanLineVerdict): {
  variant: BadgeVariant;
  label: string;
} {
  return VERDICT_BADGE[verdict];
}

/** recommendation → Reorder-table row Badge. `overstocked` is the
 *  DON'T-reorder signal the design doc calls out explicitly. */
const RECOMMENDATION_BADGE: Record<
  ReorderRecommendation,
  { variant: BadgeVariant; label: string }
> = {
  reorder_now: { variant: 'negative', label: 'Reorder now' },
  watch: { variant: 'warning', label: 'Watch' },
  ok: { variant: 'positive', label: 'OK' },
  overstocked: { variant: 'neutral', label: "Overstocked — don't reorder" },
  excluded: { variant: 'neutral', label: "Excluded — don't reorder" },
  unknown: { variant: 'neutral', label: 'Unknown' },
};

export function recommendationBadge(recommendation: ReorderRecommendation): {
  variant: BadgeVariant;
  label: string;
} {
  return RECOMMENDATION_BADGE[recommendation];
}

/** Sort weight for the Reorder table: reorder_now first, then watch, ok,
 *  overstocked, unknown last (nothing actionable there). */
const RECOMMENDATION_SORT_WEIGHT: Record<ReorderRecommendation, number> = {
  reorder_now: 0,
  watch: 1,
  ok: 2,
  overstocked: 3,
  excluded: 4,
  unknown: 5,
};

export function compareRecommendations(
  a: { recommendation: ReorderRecommendation },
  b: { recommendation: ReorderRecommendation }
): number {
  return (
    RECOMMENDATION_SORT_WEIGHT[a.recommendation] - RECOMMENDATION_SORT_WEIGHT[b.recommendation]
  );
}

export function money(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/** Same cents-in, USD-out contract as money() above, but no cents shown
 *  (e.g. "$2,000" not "$2,000.00") — for the "Profit/mo" column (Faraz
 *  2026-09-27), a per-month total where the exact cent doesn't matter. */
export function moneyWhole(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export function percent(pct: number | null | undefined): string {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${pct.toFixed(1)}%`;
}

/**
 * Builds the `points` attribute for an SVG `<polyline>` — a tiny inline
 * sparkline of price history, no chart library. `points` are in
 * chronological/insertion order (research.history is already ≤90d,
 * downsampled to ≤30 points, oldest first). Pure and total: an empty series
 * returns '' (renders nothing); a single point centers itself so the
 * polyline still has valid coordinates.
 */
export function sparklinePath(
  points: { t: number; priceCents: number }[],
  w: number,
  h: number
): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    return `${(w / 2).toFixed(2)},${(h / 2).toFixed(2)}`;
  }
  const ts = points.map((p) => p.t);
  const prices = points.map((p) => p.priceCents);
  const minT = Math.min(...ts);
  const maxT = Math.max(...ts);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const spanT = maxT - minT || 1;
  const spanP = maxP - minP || 1;
  return points
    .map((p) => {
      const x = ((p.t - minT) / spanT) * w;
      // SVG y grows downward — invert so a higher price sits nearer the top.
      const y = h - ((p.priceCents - minP) / spanP) * h;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
}

// ─────────────────────────────────────────────────────────
// Reorder table filter pills (live-feedback follow-up)
// ─────────────────────────────────────────────────────────

/** `needs_attention` is a synthetic combined bucket (reorder_now + watch),
 *  not a literal `recommendation` value — it's the DEFAULT pill so the VA
 *  lands on "what needs a decision today" instead of the full unfiltered
 *  list. `all` includes 'unknown' rows, which have no dedicated pill of
 *  their own. */
export type ReorderFilterKey =
  | 'needs_attention'
  | 'all'
  | 'reorder_now'
  | 'watch'
  | 'ok'
  | 'overstocked'
  | 'excluded';

export const DEFAULT_REORDER_FILTER: ReorderFilterKey = 'needs_attention';

export const REORDER_FILTER_PILLS: { key: ReorderFilterKey; label: string }[] = [
  { key: 'needs_attention', label: 'Needs attention' },
  { key: 'all', label: 'All' },
  { key: 'reorder_now', label: 'Reorder now' },
  { key: 'watch', label: 'Watch' },
  { key: 'ok', label: 'OK' },
  { key: 'overstocked', label: 'Overstocked' },
  { key: 'excluded', label: 'Excluded' },
];

export function matchesReorderFilter(
  rec: { recommendation: ReorderRecommendation },
  filter: ReorderFilterKey
): boolean {
  if (filter === 'all') return true;
  if (filter === 'needs_attention') {
    return rec.recommendation === 'reorder_now' || rec.recommendation === 'watch';
  }
  return rec.recommendation === filter;
}

export function filterRecommendations<T extends { recommendation: ReorderRecommendation }>(
  rows: T[],
  filter: ReorderFilterKey
): T[] {
  return rows.filter((r) => matchesReorderFilter(r, filter));
}

/** Pure and total: every pill key is present even for an empty list (all
 *  zero), so callers can render "Label · 0" without a fallback branch. */
export function reorderFilterCounts(
  rows: { recommendation: ReorderRecommendation }[]
): Record<ReorderFilterKey, number> {
  const counts: Record<ReorderFilterKey, number> = {
    needs_attention: 0,
    all: rows.length,
    reorder_now: 0,
    watch: 0,
    ok: 0,
    overstocked: 0,
    excluded: 0,
  };
  for (const r of rows) {
    switch (r.recommendation) {
      case 'reorder_now':
        counts.reorder_now++;
        counts.needs_attention++;
        break;
      case 'watch':
        counts.watch++;
        counts.needs_attention++;
        break;
      case 'ok':
        counts.ok++;
        break;
      case 'overstocked':
        counts.overstocked++;
        break;
      case 'excluded':
        counts.excluded++;
        break;
      case 'unknown':
        // No dedicated pill — only reflected in `all`.
        break;
    }
  }
  return counts;
}

// ─────────────────────────────────────────────────────────
// pricing-truth arc Task B (2026-08-25) — plan-line table overhaul.
// verdictTierBadge/authorizationBadge/anchorSourceLabel/feesSourceLabel
// below extend (don't replace) the legacy verdictBadge — research.verdict
// (legacy 4-token) stays wired for anything still reading it, while the
// new table/math-card reads research.verdictTier (5-tier, always present
// post-schema-transform — see OrderPlanLineResearch's read shim in
// packages/schemas/src/api.ts).
// ─────────────────────────────────────────────────────────

const VERDICT_TIER_BADGE: Record<
  OrderPlanLineVerdictTier,
  { variant: BadgeVariant; label: string }
> = {
  underwater: { variant: 'negative', label: 'Underwater' },
  'below-floor': { variant: 'warning', label: 'Below floor' },
  'below-target': { variant: 'info', label: 'Below target' },
  optimal: { variant: 'positive', label: 'Optimal' },
  unknown: { variant: 'neutral', label: 'Unverified' },
};

export function verdictTierBadge(tier: OrderPlanLineVerdictTier): {
  variant: BadgeVariant;
  label: string;
} {
  return VERDICT_TIER_BADGE[tier];
}

/** Listings Restrictions badge — shared vocabulary with price-calibration's
 *  row badge (arc spec). `null` (line never researched, so authorization was
 *  never even attempted) reads the same as 'unknown' — there's nothing more
 *  specific to say in either case. */
const AUTHORIZATION_BADGE: Record<
  AmazonAuthorizationStatus,
  { variant: BadgeVariant; label: string }
> = {
  authorized: { variant: 'positive', label: 'Authorized' },
  approval_required: { variant: 'warning', label: 'Approval required' },
  restricted: { variant: 'negative', label: 'Restricted' },
  unknown: { variant: 'neutral', label: 'Unknown' },
};

export function authorizationBadge(status: AmazonAuthorizationStatus | null): {
  variant: BadgeVariant;
  label: string;
} {
  return AUTHORIZATION_BADGE[status ?? 'unknown'];
}

/** Anchor hierarchy source label (arc spec, Web section) — 'buy_box'/
 *  'intended'/'listed' name whichever price actually anchored the verdict;
 *  null means research ran but found no Buy Box, intended price, or synced
 *  listed price to anchor on. Callers gate on `line.research` first — this
 *  only covers what to say once research exists (a never-researched line
 *  shows its own "Not researched" copy instead).
 *
 *  'buy_box' added 2026-09-06 (Faraz): the Buy Box is now the top of the
 *  anchor hierarchy, not context-only — see
 *  packages/schemas/src/api.ts's OrderPlanLineAnchorSource doc.
 *
 *  'lowest_offer' added 2026-09-06, same day (Faraz): "if BB is suppressed,
 *  use the lowest offer as base." Shipping (when non-zero) is called out
 *  separately by mathCardPriceAnnotation below, not folded into this label —
 *  callers that only need the plain source name (e.g. the plan-line table's
 *  narrow column) still get a clean string.
 *
 *  'lowest_fba_offer' added 2026-09-11 (anchor-spread design): the Buy Box
 *  was far enough above a live FBA competitor offer that pickAnchor used the
 *  offer instead — see OrderPlanLineAnchorSpread. */
export function anchorSourceLabel(anchorSource: OrderPlanLineAnchorSource | null): string {
  switch (anchorSource) {
    case 'buy_box':
      return 'the Buy Box price';
    case 'lowest_fba_offer':
      return 'the lowest FBA offer (below Buy Box)';
    case 'lowest_offer':
      return 'the lowest offer (landed)';
    case 'intended':
      return 'your intended price';
    case 'listed':
      return 'your listed price';
    case 'keepa_price':
      return "Amazon's price (Keepa)";
    case null:
      return 'no anchor — set an intended price';
  }
}

/** Fees provenance label for the Fees column + math card's fees line. */
export function feesSourceLabel(
  feesSource: 'amazon' | 'estimated' | 'keepa_estimate' | 'floor_estimate' | 'manual' | null
): string {
  switch (feesSource) {
    case 'amazon':
      return 'Amazon';
    case 'estimated':
      return 'estimated — Amazon fees pending';
    // Task C (2026-09-25): Keepa's own catalog fee data, used only when
    // Amazon's catalog has no package dimensions to quote real fees at all
    // — see merge-provider-evidence.ts's verdict-policy doc for why this
    // can confirm a reject but never a buy.
    case 'keepa_estimate':
      return 'estimated from Keepa — no Amazon fee data for this item';
    // Smallest FBA fulfillment fee + referral, for items with no catalog
    // dimensions and no Keepa fee. Can confirm a reject, never a buy.
    case 'floor_estimate':
      return 'Fees: floor estimate';
    // Verified fee override (2026-09-30): typed in from Amazon's Revenue
    // Calculator; treated as a real fee (full verdict).
    case 'manual':
      return 'Fees: verified manually';
    case null:
      return 'not checked';
  }
}

// ─────────────────────────────────────────────────────────
// Duplicate-ASIN warning (Faraz 2026-09-17: "planning page allows to add
// duplicate ASINs. It should warn that it is already added in the plan" —
// decision: warn, don't block. Two lines for one ASIN are sometimes
// legitimate (different supplier, different pack), so the user must be able
// to proceed deliberately). Matching is by ASIN only, case-insensitive;
// `rejected` lines are excluded (not part of the plan any more, same
// convention as planLineFilterCounts above); a line falls back to
// research.asin when it has no top-level asin (same resolved-ASIN
// convention as OrderPlanLine.demand's doc); a line with no ASIN at all
// (free-text item) never counts and never warns.
// ─────────────────────────────────────────────────────────

export type AsinUsage = { lines: number; qty: number };

export function planAsinUsage(
  lines: Pick<OrderPlanLine, 'asin' | 'status' | 'qtyPlanned' | 'research'>[]
): Map<string, AsinUsage> {
  const usage = new Map<string, AsinUsage>();
  for (const line of lines) {
    if (line.status === 'rejected') continue;
    const asin = line.asin ?? line.research?.asin ?? null;
    if (!asin) continue;
    const key = asin.toUpperCase();
    const existing = usage.get(key);
    if (existing) {
      existing.lines += 1;
      existing.qty += line.qtyPlanned;
    } else {
      usage.set(key, { lines: 1, qty: line.qtyPlanned });
    }
  }
  return usage;
}

/** Case-insensitive lookup into planAsinUsage's map — null for no ASIN or no
 *  match, so every caller (the search-result badge, the fields-step notice,
 *  the submit warning) shares one normalization instead of each re-deriving
 *  `.toUpperCase()`. */
export function lookupAsinUsage(
  usage: Map<string, AsinUsage>,
  asin: string | null | undefined
): AsinUsage | null {
  if (!asin) return null;
  return usage.get(asin.toUpperCase()) ?? null;
}

// ─────────────────────────────────────────────────────────
// Plan-line dollar inputs (cost + intended price) — one parser shared by
// both edit affordances. `ok: true, cents: null` means "the user cleared
// the field" (both fields are nullable on the wire); `ok: false` means
// reject and revert the input, same UX both fields already had.
// ─────────────────────────────────────────────────────────

export type DollarInputParseResult = { ok: true; cents: number | null } | { ok: false };

export function parseDollarInput(text: string): DollarInputParseResult {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: true, cents: null };
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars < 0) return { ok: false };
  return { ok: true, cents: Math.round(dollars * 100) };
}

/** intendedPriceCents is `.positive()` on the wire (UpdateOrderPlanLineRequest)
 *  — unlike cost, 0 isn't a valid override (a $0 intended price isn't a real
 *  anchor, it's a mistake), so this rejects it instead of accepting it as a
 *  clear-the-field value (empty text already covers clearing). */
export function parseIntendedPriceInput(text: string): DollarInputParseResult {
  const result = parseDollarInput(text);
  if (!result.ok) return result;
  if (result.cents !== null && result.cents <= 0) return { ok: false };
  return result;
}

/** True when a PATCH that changed intendedPriceCents also invalidated the
 *  line's stored research (route nulls `research` whenever intendedPriceCents
 *  actually changes — see routes/order-planning.ts's PATCH /lines/:id). Pure
 *  so the "research invalidated — check again" row state is derived from the
 *  before/after response pair rather than guessed client-side. */
export function researchInvalidatedByPriceChange(
  prevResearch: OrderPlanLineResearch | null,
  nextResearch: OrderPlanLineResearch | null
): boolean {
  return prevResearch !== null && nextResearch === null;
}

// ─────────────────────────────────────────────────────────
// Plan-line filter pills — All / Underwater / Below floor / Needs research /
// Approval required (arc spec, Web section). Distinct filter set from the
// Reorder table's ReorderFilterKey above — different rows, different
// buckets. Default 'all': unlike Reorder's large recommendation list, a
// plan's line list is small and VA-curated — landing on the full list reads
// better than pre-filtering it (same default choice as price-calibration.ts).
// ─────────────────────────────────────────────────────────

export type PlanLineFilterKey =
  | 'all'
  | 'underwater'
  | 'below-floor'
  | 'needs-research'
  | 'approval-required';

export const DEFAULT_PLAN_LINE_FILTER: PlanLineFilterKey = 'all';

export const PLAN_LINE_FILTER_PILLS: { key: PlanLineFilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'underwater', label: 'Underwater' },
  { key: 'below-floor', label: 'Below floor' },
  { key: 'needs-research', label: 'Needs research' },
  { key: 'approval-required', label: 'Approval required' },
];

type PlanLineFilterRow = Pick<OrderPlanLine, 'research'>;

export function matchesPlanLineFilter(row: PlanLineFilterRow, filter: PlanLineFilterKey): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'underwater':
      return row.research?.verdictTier === 'underwater';
    case 'below-floor':
      return row.research?.verdictTier === 'below-floor';
    case 'needs-research':
      return row.research === null;
    case 'approval-required':
      return row.research?.authorization === 'approval_required';
  }
}

export function filterPlanLines<T extends PlanLineFilterRow>(
  rows: T[],
  filter: PlanLineFilterKey
): T[] {
  return rows.filter((r) => matchesPlanLineFilter(r, filter));
}

/** Pure and total: every pill key present even for an empty list. */
export function planLineFilterCounts(rows: PlanLineFilterRow[]): Record<PlanLineFilterKey, number> {
  const counts: Record<PlanLineFilterKey, number> = {
    all: rows.length,
    underwater: 0,
    'below-floor': 0,
    'needs-research': 0,
    'approval-required': 0,
  };
  for (const r of rows) {
    if (r.research === null) counts['needs-research']++;
    if (r.research?.verdictTier === 'underwater') counts.underwater++;
    if (r.research?.verdictTier === 'below-floor') counts['below-floor']++;
    if (r.research?.authorization === 'approval_required') counts['approval-required']++;
  }
  return counts;
}

// ─────────────────────────────────────────────────────────
// Plan-line search — description, sellerSku, asin, upc, itemNo.
// ─────────────────────────────────────────────────────────

type PlanLineSearchRow = Pick<
  OrderPlanLine,
  'description' | 'sellerSku' | 'asin' | 'upc' | 'itemNo'
>;

export function matchesPlanLineSearch(row: PlanLineSearchRow, query: string): boolean {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return [row.description, row.sellerSku, row.asin, row.upc, row.itemNo]
    .filter((v): v is string => Boolean(v))
    .some((v) => v.toLowerCase().includes(trimmed));
}

export function searchPlanLines<T extends PlanLineSearchRow>(rows: T[], query: string): T[] {
  return rows.filter((r) => matchesPlanLineSearch(r, query));
}

// ─────────────────────────────────────────────────────────
// Math card — the expandable per-line arithmetic block (arc spec: "price −
// fees − cost = margin, each line with source + age annotation, thresholds
// line with the tenant's live percentages, verdict as the last line; buy-box
// context line when the row has one; trendWarning as a warning line").
// Deliberately keyed on a small explicit input shape (MathCardInput) rather
// than OrderPlanLineResearch directly — Task C reuses the same MathCard
// component/shape for price-calibration rows via its own adapter, per the
// arc spec's "Shared component used by BOTH PlanningPage and
// PriceCalibrationPage."
// ─────────────────────────────────────────────────────────

export interface MathCardInput {
  anchorPriceCents: number | null;
  anchorSource: OrderPlanLineAnchorSource | null;
  /** Shipping half of a 'lowest_offer' anchor's landed price (2026-09-06) —
   *  only meaningful when anchorSource is 'lowest_offer'; the price line
   *  calls it out ("incl. $4.99 shipping") only when this is a positive
   *  number, per Faraz's "count shipping if any" (an FBA/Prime offer's $0
   *  shipping earns no clause). Undefined for every other anchorSource, or
   *  for research stored before this field existed — same
   *  optional/undefined-vs-null convention as prepCents below. */
  lowestOfferShippingCents?: number | null;
  feesCents: number | null;
  feesSource: 'amazon' | 'estimated' | 'keepa_estimate' | 'floor_estimate' | 'manual' | null;
  /** Set only alongside feesSource 'keepa_estimate' — Keepa's own reported
   *  snapshot time for the fbaFees this estimate was built from (2026-09-25
   *  Task C follow-up, MINOR). Undefined for every other feesSource,
   *  including research stored before this field existed. */
  keepaFeeLastUpdate?: string | null;
  costCents: number | null;
  costSource: { poExternalId: string | null; invoiceDate: string | null } | null;
  /** Multipack fix (2026-08-31) — provenance for `costCents` when it's a
   *  listing-level cost: 'landed' = the canonical landed cost
   *  (price-calibration rows only); 'supplier_x_pack' = a vendor/supplier
   *  unit cost x unitsPerListing (both price-calibration and, since
   *  dd5533e's downstream twin fix, order-planning research rows — the
   *  latter never produces 'landed', only 'supplier_x_pack' or null).
   *  Undefined/null falls back to the pre-existing costSource-based
   *  annotation below, unchanged (e.g. research JSON stored before this
   *  fix existed — deploy skew). */
  costBasis?: 'landed' | 'supplier_x_pack' | null;
  /** unitsPerListing for the supplier_x_pack annotation's "x N" — only
   *  meaningful alongside costBasis: 'supplier_x_pack'. */
  unitsPerListing?: number | null;
  /** R2 item 3 (D4, docs/plans/2026-09-03-graded-kernel.md) — the ONE
   *  kernel grade vocabulary's source for `costCents`/`costBasis`:
   *  'lot_landed' = a received lot's true base+landed-charges cost
   *  (settlement-grade, lib/finance/landed-cost.ts). Undefined/null adds
   *  no annotation (order-planning rows, and price-calibration rows before
   *  this field existed). */
  costBasisSource?: 'lot_landed' | 'supplier_pack' | null;
  /** Prep-per-listing already subtracted into `marginCents` upstream
   *  (research.ts's computeMarginVerdict: margin = price − fees − cost −
   *  prep) — without its own card line the price/fees/cost lines no longer
   *  summed to the margin shown. Optional/undefined (not just null) is
   *  deliberate: undefined means this MathCardInput shape doesn't track
   *  prep at all (price-calibration rows, pre-fix stored research) and the
   *  line is omitted entirely, vs. null meaning it's tracked but unknown —
   *  order-planning's planLineToMathCardInput always sets one or the other,
   *  never leaves it undefined. Source: research.marketCheck.prepPerListingCents
   *  (2026-09-05 lead-with-total fix). */
  prepCents?: number | null;
  marginCents: number | null;
  marginFrac: number | null;
  thresholds: { minFrac: number; targetFrac: number } | null;
  trendWarning: boolean;
  trend: { pctChange: number; spanDays: number } | null;
  verdictTier: OrderPlanLineVerdictTier;
  /** ISO timestamp this research ran — the "age" half of every line's
   *  source+age annotation except cost (cost ages off its own invoice date). */
  checkedAt: string;
  note: string | null;
  /** Context-only line, e.g. "Buy box $19.99 (Amazon holds it)" — planner
   *  rows never have one (buy box isn't tracked on order_plan_lines);
   *  price-calibration rows will (Task C). */
  buyBoxLine?: string | null;
  /** Listings Restrictions status (Task C addendum) — renders as a card
   *  line ONLY when not 'authorized' (an all-clear needs no line; a
   *  restriction does). Optional/absent for planner rows, which surface
   *  authorization in their own table column instead. */
  authorization?: AmazonAuthorizationStatus | null;
}

export interface MathCardLine {
  label: string;
  sign: '' | '+' | '−' | '=';
  amount: string;
  annotation: string;
}

export interface MathCardData {
  lines: MathCardLine[];
  thresholdsLine: string | null;
  trendWarningLine: string | null;
  buyBoxLine: string | null;
  /** "Authorization: Approval required" etc. — null when authorized or when
   *  the input carries no authorization at all (planner rows). */
  authorizationLine: string | null;
  verdictLine: string;
}

/** Card line for a non-authorized Listings Restrictions status. Pure and
 *  total: 'authorized' and null/undefined (not tracked on this surface)
 *  both return null — only an actual restriction/unknown earns a line. */
export function authorizationCardLine(
  status: AmazonAuthorizationStatus | null | undefined
): string | null {
  if (status == null || status === 'authorized') return null;
  return `Authorization: ${AUTHORIZATION_BADGE[status].label}`;
}

function mathCardPriceAnnotation(input: MathCardInput): string {
  // "Count shipping if any" (Faraz, 2026-09-06) — a truthy check excludes
  // undefined (not tracked), null (unknown), AND 0 (FBA/Prime offers ship
  // free) in one go; only a real positive shipping cost earns the clause.
  const shippingSuffix =
    input.anchorSource === 'lowest_offer' && input.lowestOfferShippingCents
      ? ` incl. ${money(input.lowestOfferShippingCents)} shipping`
      : '';
  const label = anchorSourceLabel(input.anchorSource) + shippingSuffix;
  if (input.anchorPriceCents === null) return label;
  return `${label} · checked ${shortDateLabel(input.checkedAt)}`;
}

function mathCardFeesAnnotation(input: MathCardInput): string {
  if (input.feesSource === 'amazon') return `Amazon · checked ${shortDateLabel(input.checkedAt)}`;
  if (input.feesSource === 'estimated') return feesSourceLabel('estimated');
  // Task C follow-up (2026-09-25, MINOR): keepaFeeLastUpdate is Keepa's own
  // snapshot time for the fbaFees this lower-bound estimate was built from
  // — surfaced as a staleness hint when present, same shortDateLabel the
  // 'amazon' branch above already uses.
  if (input.feesSource === 'keepa_estimate') {
    return input.keepaFeeLastUpdate
      ? `${feesSourceLabel('keepa_estimate')} · Keepa data from ${shortDateLabel(input.keepaFeeLastUpdate)}`
      : feesSourceLabel('keepa_estimate');
  }
  if (input.feesSource === 'floor_estimate')
    return 'Smallest FBA fee plus referral. Confirms a loss only.';
  if (input.feesSource === 'manual') return "Entered from Amazon's Revenue Calculator.";
  return feesSourceLabel(null);
}

function mathCardCostAnnotation(input: MathCardInput): string {
  // Price-calibration multipack fix (2026-08-31) — costCents there is
  // always a LISTING-level cost; say so, and call out the pack multiplier
  // when the fallback applied one, so the card never implies a bare
  // per-vendor-unit cost when it isn't one.
  if (input.costBasis === 'landed') {
    // R2 item 3 (D4): a positive-only signal — 'lot_landed' means this
    // number is proven settlement-grade (base + landed-cost charge
    // allocations, not just base cost). No other costBasisSource value
    // changes this annotation, so existing (order-planning and pre-field
    // price-calibration) callers see the exact same string as before.
    return input.costBasisSource === 'lot_landed'
      ? 'landed cost per listing (settlement-grade)'
      : 'landed cost per listing';
  }
  if (input.costBasis === 'supplier_x_pack') {
    return input.unitsPerListing && input.unitsPerListing > 1
      ? `supplier cost × ${input.unitsPerListing} per listing`
      : 'supplier cost per listing';
  }
  if (input.costSource?.poExternalId) {
    return input.costSource.invoiceDate
      ? `invoice ${input.costSource.poExternalId} · ${shortDateLabel(input.costSource.invoiceDate)}`
      : `invoice ${input.costSource.poExternalId}`;
  }
  return input.costCents !== null ? 'cost on file · source unknown' : 'no cost on file';
}

function mathCardPrepAnnotation(input: MathCardInput): string {
  return input.prepCents !== null ? 'prep rate on file' : 'no prep rate on file';
}

function mathCardMarginAnnotation(input: MathCardInput): string {
  if (input.marginFrac !== null) return `${marginPercentLabel(input.marginFrac)} margin`;
  return input.note ?? 'unknown — missing anchor price, cost, or fees';
}

/** Fraction (0-1, tenants.min_margin_pct convention) -> one-decimal percent
 *  string. Local to this module (order-planning.ts's own `percent()` above
 *  takes an already-0-100 number for the legacy research.marginPct display —
 *  a different convention for a different caller; this one matches
 *  price-calibration.ts's marginPercent so the math card's numbers read the
 *  same on both pages once Task C wires it up there too). */
function marginPercentLabel(fraction: number): string {
  return `${(fraction * 100).toFixed(1)}%`;
}

export function buildMathCard(input: MathCardInput): MathCardData {
  const lines: MathCardLine[] = [
    {
      label: 'price',
      sign: '',
      amount: money(input.anchorPriceCents),
      annotation: mathCardPriceAnnotation(input),
    },
    {
      label: 'fees',
      sign: '−',
      amount: money(input.feesCents),
      annotation: mathCardFeesAnnotation(input),
    },
    {
      label: 'cost',
      sign: '−',
      amount: money(input.costCents),
      annotation: mathCardCostAnnotation(input),
    },
    // Only rendered when this MathCardInput shape tracks prep at all
    // (order-planning rows, always, as of the 2026-09-05 fix) — see the
    // field doc on MathCardInput.prepCents. Undefined (price-calibration,
    // or research stored before the fix) keeps the card exactly as before.
    ...(input.prepCents !== undefined
      ? [
          {
            label: 'prep',
            sign: '−' as const,
            amount: money(input.prepCents),
            annotation: mathCardPrepAnnotation(input),
          },
        ]
      : []),
    {
      label: 'margin',
      sign: '=',
      amount: money(input.marginCents),
      annotation: mathCardMarginAnnotation(input),
    },
  ];

  const thresholdsLine = input.thresholds
    ? `floor ${marginPercentLabel(input.thresholds.minFrac)} · target ${marginPercentLabel(input.thresholds.targetFrac)}`
    : null;

  const trendWarningLine = input.trendWarning
    ? input.trend
      ? `⚠ price declining ${Math.abs(input.trend.pctChange).toFixed(0)}% over ${input.trend.spanDays}d`
      : '⚠ price declining over 90d'
    : null;

  return {
    lines,
    thresholdsLine,
    trendWarningLine,
    buyBoxLine: input.buyBoxLine ?? null,
    authorizationLine: authorizationCardLine(input.authorization),
    verdictLine: `Verdict: ${verdictTierBadge(input.verdictTier).label}`,
  };
}

/** Adapter from the wire OrderPlanLine to MathCardInput — null when the line
 *  has never been researched (nothing to build a math card from yet; the
 *  row shows "Not researched" instead of an expand affordance in that case). */
export function planLineToMathCardInput(
  line: Pick<OrderPlanLine, 'research' | 'expectedUnitCostCents'>
): MathCardInput | null {
  const r = line.research;
  if (!r) return null;
  return {
    anchorPriceCents: r.anchorPriceCents,
    anchorSource: r.anchorSource,
    // Present (even as null) only for a 'lowest_offer' anchor — every other
    // anchorSource, and research stored before this field existed, leaves
    // this key undefined (see MathCardInput.lowestOfferShippingCents doc).
    ...(r.anchorSource === 'lowest_offer'
      ? { lowestOfferShippingCents: r.lowestOffer?.shippingCents ?? null }
      : {}),
    feesCents: r.estFeesCents,
    feesSource: r.feesSource,
    ...(r.feesSource === 'keepa_estimate'
      ? { keepaFeeLastUpdate: r.feesBreakdown?.keepaFeeLastUpdate ?? null }
      : {}),
    // Multipack fix (2026-08-31, downstream twin of price-calibration's
    // dd5533e) — r.costCents is the LISTING-level cost the verdict was
    // actually computed from (expectedUnitCostCents x unitsPerListing).
    // Falls back to the raw vendor-unit cost only for research stored
    // before this field existed (deploy skew) — same display as before.
    costCents: r.costCents ?? line.expectedUnitCostCents,
    costBasis: r.costBasis,
    unitsPerListing: r.unitsPerListing,
    costSource: r.costSource,
    // Same value r.estMarginCents already had subtracted (research.ts's
    // computeMarginVerdict) — surfaced as its own card line so the card's
    // arithmetic is visible, not just the already-net margin.
    prepCents: r.marketCheck?.prepPerListingCents ?? null,
    marginCents: r.estMarginCents,
    marginFrac: r.marginPct !== null ? r.marginPct / 100 : null,
    thresholds: r.thresholds,
    trendWarning: r.trendWarning,
    trend: r.trend ? { pctChange: r.trend.pctChange, spanDays: r.trend.spanDays } : null,
    verdictTier: r.verdictTier,
    checkedAt: r.checkedAt,
    note: r.note,
  };
}

// ─────────────────────────────────────────────────────────
// Plan-line row lead summary (2026-09-05 "lead with the line total" fix —
// Faraz found the row confusing because it opened with per-unit math
// instead of what the line actually does to the order: units in, dollars
// out, dollars back). "out" = qtyPlanned x expectedUnitCostCents — always
// computable from the line itself. "back" is the planner's freight-
// allocated profit for the line (buying.proposal.economics.profitCents) —
// it ONLY exists on planner-generated lines, because freight is only
// allocated once the nightly worker assembles the whole order (see
// buying-math.ts's allocateFreight). A hand-added line has no `buying`
// payload at all, so backCents stays honestly null rather than a freight-
// free number dressed up as the same figure — the per-unit fallback in
// that case is the research margin (fees + prep, no freight), and
// perUnitIncludesFreight tells the caller which one it got so it can label
// it accurately.
// ─────────────────────────────────────────────────────────

export interface PlanLineLeadSummary {
  qtyPlanned: number;
  /** qtyPlanned x expectedUnitCostCents; null when no cost is on file. */
  outCents: number | null;
  /** buying.proposal.economics.profitCents — null on a hand-added line
   *  (no planner freight allocation to report). */
  backCents: number | null;
  /** Per-unit profit for the line's second line of text: backCents /
   *  qtyPlanned when a planner payload exists (freight-inclusive — same
   *  ratio the $-out/$-back pair on screen implies), else
   *  research.estMarginCents (fees + prep, no freight) — null when
   *  neither is available (never researched, hand-added line). */
  perUnitCents: number | null;
  /** True only when perUnitCents is the planner's freight-allocated
   *  figure — false means it's the research margin, and callers must say
   *  so ("after fees, prep" without freight) rather than implying parity
   *  with the planner number. */
  perUnitIncludesFreight: boolean;
  /** research.marginPct (0-100 scale, same figure the Research page shows —
   *  "margin after fees") — set only on the research path
   *  (perUnitIncludesFreight false); always null on the buying/freight-
   *  inclusive path, which has no comparable revenue-anchored figure
   *  (2026-09-16 "add % with estimated profit" request). */
  marginPct: number | null;
}

export function planLineLeadSummary(
  line: Pick<OrderPlanLine, 'qtyPlanned' | 'expectedUnitCostCents' | 'buying' | 'research'>
): PlanLineLeadSummary {
  const outCents =
    line.expectedUnitCostCents !== null ? line.qtyPlanned * line.expectedUnitCostCents : null;
  const profitCents = line.buying?.proposal.economics.profitCents ?? null;
  if (profitCents !== null) {
    return {
      qtyPlanned: line.qtyPlanned,
      outCents,
      backCents: profitCents,
      perUnitCents: line.qtyPlanned > 0 ? Math.round(profitCents / line.qtyPlanned) : null,
      perUnitIncludesFreight: true,
      marginPct: null,
    };
  }
  return {
    qtyPlanned: line.qtyPlanned,
    outCents,
    backCents: null,
    perUnitCents: line.research?.estMarginCents ?? null,
    perUnitIncludesFreight: false,
    marginPct: line.research?.marginPct ?? null,
  };
}

/** Research profit is per Amazon listing; quantities are supplier units. */
export function planLineProfitEstimate(line: OrderPlanLine) {
  const assembled = line.buying?.proposal.economics.profitCents;
  if (assembled != null) return { cents: assembled, beforeFreight: false, leftoverUnits: 0 };
  const pack = line.research?.unitsPerListing;
  const margin = line.research?.estMarginCents;
  if (pack == null || pack <= 0 || margin == null)
    return { cents: null, beforeFreight: true, leftoverUnits: 0 };
  return {
    cents: Math.floor(line.qtyPlanned / pack) * margin,
    beforeFreight: true,
    leftoverUnits: line.qtyPlanned % pack,
  };
}

/**
 * Plan-total margin % (2026-09-16 "add % with estimated profit" request) —
 * total profit ÷ total revenue across every plan line counted in the money
 * summary's profit total (planLineProfitEstimate(line).cents !== null),
 * 0-100 scale (same convention as research.marginPct; format with percent()).
 *
 * Revenue only exists for the research path: completeListings (floor(qty /
 * unitsPerListing)) × research.anchorPriceCents. A line whose profit came
 * from the assembled buying path (buying.proposal.economics.profitCents) has
 * no comparable revenue figure — if ANY counted line takes that path, or a
 * counted research line is missing a usable anchor price/pack size, the
 * whole total is unknowable and this returns null rather than a partial
 * (misleadingly precise) number. Also null when nothing ends up contributing
 * revenue (e.g. every line excluded or unresearched).
 */
export function planSummaryMarginPct(lines: OrderPlanLine[]): number | null {
  const included = lines.filter((l) => l.status !== 'rejected' && !l.holdReason);
  let totalProfitCents = 0;
  let totalRevenueCents = 0;
  for (const line of included) {
    const estimate = planLineProfitEstimate(line);
    if (estimate.cents === null) continue; // not counted in the profit total at all
    if (line.buying?.proposal.economics.profitCents != null) return null; // no revenue figure
    const anchor = line.research?.anchorPriceCents;
    const pack = line.research?.unitsPerListing;
    if (anchor == null || anchor <= 0 || pack == null || pack <= 0) return null;
    const completeListings = Math.floor(line.qtyPlanned / pack);
    totalProfitCents += estimate.cents;
    totalRevenueCents += completeListings * anchor;
  }
  if (totalRevenueCents === 0) return null;
  return (totalProfitCents / totalRevenueCents) * 100;
}

/** Prep is the user's combined handling/shipping allowance; label profits as estimates. */
export function planLinePerUnitLabel(summary: PlanLineLeadSummary): string {
  if (summary.perUnitCents === null) return 'Per-unit profit unknown — check margin to see it.';
  const base = `Estimated profit: ${money(summary.perUnitCents)}/${summary.perUnitIncludesFreight ? 'unit' : 'listing'}`;
  // marginPct is only ever set on the research path (see PlanLineLeadSummary
  // doc) — the check here is belt-and-suspenders, not load-bearing.
  if (!summary.perUnitIncludesFreight && summary.marginPct !== null) {
    return `${base} · ${percent(summary.marginPct)} margin`;
  }
  return base;
}

/** Shown under the lead line whenever backCents is null — the honest-null
 *  explanation for why a hand-added line has no line-total figure. */
export const LINE_TOTAL_NOT_COMPUTED_NOTE =
  'Estimated profit unavailable — check pricing and pack size.';

// ─────────────────────────────────────────────────────────
// Research button — stepped progress (arc spec: "Checking Amazon price…" ->
// "Getting Amazon's fees…" -> "Checking authorization…", disabled while
// running, inline per-step failure). True streaming is out of scope (a
// single response, per research.ts's module doc) — RESEARCH_PROGRESS_STEPS
// is the OPTIMISTIC list shown for the whole in-flight window, not a
// real-time tracker; researchStepFailures reads the real per-step results
// once the response lands.
// ─────────────────────────────────────────────────────────

type AmazonResearchStep = 'price' | 'fees' | 'authorization';

const RESEARCH_STEP_PENDING_LABEL: Record<AmazonResearchStep, string> = {
  price: 'Checking Amazon price…',
  fees: "Getting Amazon's fees…",
  authorization: 'Checking authorization…',
};

export const RESEARCH_PROGRESS_STEPS: AmazonResearchStep[] = ['price', 'fees', 'authorization'];

/** Optimistic step labels shown for the whole duration a research click is
 *  in flight — a fixed, ordered list (no real-time tracking; see module doc
 *  above), so this takes no arguments. */
export function researchProgressLabels(): { step: AmazonResearchStep; label: string }[] {
  return RESEARCH_PROGRESS_STEPS.map((step) => ({
    step,
    label: RESEARCH_STEP_PENDING_LABEL[step],
  }));
}

const RESEARCH_STEP_FAILURE_LABEL: Record<OrderPlanLineResearchStep['step'], string> = {
  price: "Amazon didn't answer — try again",
  fees: "Amazon didn't answer — try again",
  authorization: "Amazon didn't answer — try again",
  verdict: "Couldn't compute a verdict — try again",
};

/** Distinct copy for a price-step failure caused specifically by Keepa
 *  running out of tokens (Phase A.1, 2026-08-31 planning-page-usability
 *  design, "Keepa token honesty") — same phrase used everywhere else this
 *  condition surfaces (KEEPA_NO_TOKENS error message, the pre-flight
 *  research.ts note). */
export const KEEPA_NO_TOKENS_STEP_LABEL = 'Keepa out of tokens — refills ~1/min';

/** Which steps of a COMPLETED research response failed, with their inline
 *  retry copy — the research still succeeded overall (feesSource falls back
 *  to 'estimated', authorization stays 'unknown', etc.), this is just what
 *  to show inline so the VA knows which number to distrust. A price-step
 *  failure carrying `code: 'keepa_no_tokens'` gets the distinct out-of-
 *  tokens copy instead of the generic "didn't answer" label. */
export function researchStepFailures(
  steps: OrderPlanLineResearchStep[]
): { step: OrderPlanLineResearchStep['step']; label: string }[] {
  return steps
    .filter((s) => !s.ok)
    .map((s) => ({
      step: s.step,
      label:
        s.code === 'keepa_no_tokens'
          ? KEEPA_NO_TOKENS_STEP_LABEL
          : RESEARCH_STEP_FAILURE_LABEL[s.step],
    }));
}

// ─────────────────────────────────────────────────────────
// Planning page usability Phase A (2026-08-31 design doc) — plain-sentence
// verdict copy, header summary sentence, and the manual-add autocomplete's
// pick → line-defaults mapping. All pure/total so the presentational
// components stay server-renderable per the repo's page-test convention.
// ─────────────────────────────────────────────────────────

const VERDICT_TIER_SENTENCE_LEAD: Record<Exclude<OrderPlanLineVerdictTier, 'unknown'>, string> = {
  underwater: 'Underwater',
  'below-floor': 'Below floor',
  'below-target': 'Below target',
  optimal: 'Profitable',
};

/**
 * Plain-English readout of a plan line's last research, for the "Check
 * margin" verdict badge's sentence (design doc item 6). `null` research
 * (never checked, OR just invalidated by a price/cost change — the PATCH
 * route nulls `research` outright on that change, see
 * researchInvalidatedByPriceChange's doc; there is no separate "stale but
 * visible" state to render differently) reads as "Not checked yet." A tier
 * of 'unknown', or a computed tier missing its price/margin (legacy/
 * deploy-skew data), falls back to the research note or a generic line —
 * same "unverifiable" treatment buildMathCard's margin annotation uses.
 */
export function researchVerdictSentence(line: Pick<OrderPlanLine, 'research'>): string {
  const r = line.research;
  if (!r) return 'Not checked yet.';
  if (r.verdictTier === 'unknown' || r.anchorPriceCents === null || r.marginPct === null) {
    return r.note ?? 'Could not verify margin — missing price, cost, or fees.';
  }
  const lead = VERDICT_TIER_SENTENCE_LEAD[r.verdictTier];
  return `${lead} at ${money(r.anchorPriceCents)} — ${r.marginPct.toFixed(0)}% margin after fees.`;
}

/**
 * The plan header's summary sentence (design doc item 5), replacing the old
 * "planned $X · approved $Y" fragment: "N items · $X planned · nothing
 * approved yet" / "· $Y approved of $X".
 */
export function planHeaderSummary(input: {
  itemCount: number;
  plannedCents: number;
  approvedCents: number;
}): string {
  const itemsLabel = `${input.itemCount} item${input.itemCount === 1 ? '' : 's'}`;
  const approvedLabel =
    input.approvedCents > 0
      ? `${money(input.approvedCents)} approved of ${money(input.plannedCents)}`
      : 'nothing approved yet';
  return `${itemsLabel} · ${money(input.plannedCents)} planned · ${approvedLabel}`;
}

/**
 * AddLineForm's product-search pick → CreateOrderPlanLineRequest defaults
 * (design doc item 4; extended 2026-09-16 for research-linked matches, which
 * have no sellerSku). Description falls back to the sellerSku, then the
 * asin, when the result has no productName on file; sellerSku, asin, and
 * researchCandidateId are all omitted (not spread as `undefined`) when the
 * result has none, matching CreateOrderPlanLineRequest's optional-field
 * convention.
 */
export function productSearchResultToLineDefaults(result: ProductSearchResult): {
  description: string;
  sellerSku?: string;
  asin?: string;
  researchCandidateId?: string;
} {
  return {
    description: result.productName?.trim() || result.sellerSku || result.asin || '',
    ...(result.sellerSku ? { sellerSku: result.sellerSku } : {}),
    ...(result.asin ? { asin: result.asin } : {}),
    ...(result.researchCandidateId ? { researchCandidateId: result.researchCandidateId } : {}),
  };
}

/**
 * AddLineForm's onSubmit handler (bug fix 2026-09-16 review): a
 * research-linked add already has its research copied server-side from the
 * saved candidate at line-create time. Auto-firing the usual post-add live
 * Keepa/Gemini re-check on top of that would both burn budget the research
 * route's 60s-per-line cooldown doesn't protect against (the saved research
 * can be hours old) and immediately overwrite the just-copied research with
 * a fresh live one. Only a plain manual/free-text add (no researchCandidateId)
 * should auto-research.
 */
export function shouldAutoResearchNewLine(input: { researchCandidateId?: string }): boolean {
  return !input.researchCandidateId;
}

/**
 * AddLineForm's handleSubmit (same 2026-09-16 review): the Expected cost
 * input is disabled once researchCandidateId is set (the server always uses
 * the saved candidate's own supplier cost for that line), but a leftover
 * parsed value in `values.cost` must never reach the request either —
 * belt-and-suspenders alongside the disabled input.
 */
export function manualLineCostCentsToSend(
  researchCandidateId: string | null,
  parsedCostCents: number | undefined
): number | undefined {
  return researchCandidateId ? undefined : parsedCostCents;
}

// ─────────────────────────────────────────────────────────
// Phase A.1 — Keepa token honesty (2026-08-31 planning-page-usability
// design). tokensLeft throughout is the same estimate GET /keepa-budget and
// the research response's `keepaTokensLeft` field carry: null means
// unknown/BYOK-unknown (never rendered/never blocks), a number is the
// platform key's best-effort bucket level.
// ─────────────────────────────────────────────────────────

/** "~N Keepa tokens" near the "Check margin" button when the budget is
 *  known; null (hidden) when unknown. Clamps a negative estimate to 0 for
 *  display — the underlying number can go negative (Keepa's own balance
 *  does), but "~-5 Keepa tokens" would read as a typo, not a warning. */
export function keepaBudgetLabel(tokensLeft: number | null): string | null {
  if (tokensLeft === null) return null;
  return `~${Math.max(0, tokensLeft)} Keepa tokens`;
}

/** True when the known estimate says the platform key has no tokens left
 *  right now — callers use this to disable "Check margin" proactively
 *  instead of letting the click fail. Never true for an unknown budget
 *  (null) — an unknown budget is "don't know," not "assume empty." */
export function keepaOutOfTokens(tokensLeft: number | null): boolean {
  return tokensLeft !== null && tokensLeft <= 0;
}

/** "out of tokens — retry in ~Ns" — the disabled-button countdown copy
 *  (design doc item 3's exact phrasing). Rounds up to the next whole
 *  second and clamps at 0 so a countdown never displays negative. */
export function keepaRetryCountdownLabel(secondsRemaining: number): string {
  return `out of tokens — retry in ~${Math.max(0, Math.ceil(secondsRemaining))}s`;
}

/** Web-side mirror of the API's keepa/token-bucket.ts secondsUntilTokens-
 *  Available — same 1-token/min refill rate, so a proactive "known estimate
 *  is already zero" state can show the same countdown math as a reactive
 *  KEEPA_NO_TOKENS response, without waiting for a click. */
export function keepaSecondsUntilAvailable(tokensLeft: number, needed: number = 1): number {
  if (tokensLeft >= needed) return 0;
  return Math.ceil(needed - tokensLeft) * 60;
}

// ─────────────────────────────────────────────────────────
// Research verdict (2026-09-11 research-verdict-simplification design,
// Faraz: "We made research way complicated ... We have a list. We find with
// compare with Amazon and Keepa and find the products.") — the tabs
// (Buy/Fix first/Everything), the Reason select, the run header's
// "Buy N · Fix first N · No N" line, and the row Reason column. Replaces the
// page's old tier/gating/identity/pack/velocity/flags/amazonRetail filter
// selects entirely (verdictTierBadge/anchorSourceLabel above stay in use for
// row rendering; the gating/identity/pack/discount/velocity/amazonRetail
// chip+label helpers further below stay too — the row panel reads them
// directly). See planning-run.ts's PlanningVerdictReason doc for the 13-code
// vocabulary this mirrors.
// ─────────────────────────────────────────────────────────

const VERDICT_BUCKET_LABEL: Record<PlanningVerdictBucket, string> = {
  buy: 'Buy',
  fix: 'Fix first',
  no: 'No',
};

export function verdictBucketLabel(bucket: PlanningVerdictBucket): string {
  return VERDICT_BUCKET_LABEL[bucket];
}

/** Reason code -> the design doc's plain "Words" column, sentence case, no
 *  jargon — the Reason column and the row panel's Verdict headline both read
 *  this directly. Total over all 13 codes (the schema's closed enum), so a
 *  lookup here never falls through to a fallback string. */
const VERDICT_REASON_WORDS: Record<PlanningVerdictReason, string> = {
  incomplete_economics: 'Complete price, cost and fees',
  check_eligibility: 'Check selling eligibility',
  no_listing: 'No Amazon listing',
  amazon_sells: 'Amazon sells it',
  loses_money: 'Loses money',
  below_floor: 'Below margin floor',
  not_eligible: 'Not eligible to sell',
  wrong_listing: 'Matched the wrong listing',
  too_slow: 'Sells under 8 a month',
  apply_approval: 'Apply for approval',
  confirm_pack: 'Confirm pack',
  slow_seller: 'Sells 8 to 15 a month',
  price_spike: 'Price spiked, wait',
  price_unstable: 'Price unstable, wait',
  check_match: 'Check listing match',
  ok: 'Ready to buy',
};

export function verdictReasonWords(reason: PlanningVerdictReason): string {
  return VERDICT_REASON_WORDS[reason];
}

/** The run header's "Buy N · Fix first N · No N" line — whole-run counts,
 *  same "always show a number, never hide the line" convention the old
 *  buyableAtSentence/provenOfBuyableSentence/amazonSellsSentence sentences
 *  (removed with this design) followed. */
export function verdictCountsLine(counts: PlanningResearchVerdictCounts): string {
  return `Buy ${counts.buy} · Fix first ${counts.fix} · No ${counts.no}`;
}

/** Fix-bucket reason codes, in the design doc's rule order (8-12) — the
 *  Reason select's fixed option list, shown only on the Fix first tab. */
export const FIX_REASON_CODES: PlanningVerdictReason[] = [
  'incomplete_economics',
  'confirm_pack',
  'slow_seller',
  'price_spike',
  'price_unstable',
  'check_match',
];

/** "Confirm pack (4)" — the Reason select's option label; a code absent from
 *  the run-wide `counts.reason` record (nobody hit that rule this run) reads
 *  as 0 rather than throwing. */
export function reasonFilterOptionLabel(
  reason: PlanningVerdictReason,
  reasonCounts: Record<string, number>
): string {
  return `${verdictReasonWords(reason)} (${reasonCounts[reason] ?? 0})`;
}

/** Short (non-sentence) anchor-source words for the research page's Buy Box
 *  column caption and the row panel's Anchor line — distinct from
 *  anchorSourceLabel above, whose "the Buy Box price" phrasing is built for
 *  the plan-line math card's sentences, not a "$X" prefix. */
const RESEARCH_ANCHOR_SOURCE_LABEL: Record<PlanningResearchAnchorSource, string> = {
  buy_box: 'Buy Box',
  lowest_fba_offer: 'Lowest FBA offer',
  lowest_offer: 'Lowest offer',
  intended: 'Intended price',
  listed: 'Listed price',
  keepa_price: "Amazon's price (Keepa)",
};

/** Research page Buy Box cell caption (2026-09-11 anchor-spread design) —
 *  shown only when the row's anchor came from the lowest-FBA-offer spread
 *  rule instead of the Buy Box itself. */
export function isLowestFbaOfferAnchor(anchorSource: PlanningResearchAnchorSource | null): boolean {
  return anchorSource === 'lowest_fba_offer';
}

/** The row panel's Verdict section "Anchor" line (2026-09-11 anchor-spread
 *  design): "Lowest FBA offer $13.25 · Buy Box $24.91 (88% above)" when the
 *  row carries a spread, else "Buy Box $X" (or whichever source anchored the
 *  row) — always a real amount when one is known, never hidden. */
export function researchAnchorLine(row: {
  anchorPriceCents: number | null;
  anchorSource: PlanningResearchAnchorSource | null;
  anchorSpread: PlanningResearchAnchorSpread | null;
}): string {
  if (row.anchorSpread) {
    return (
      `Lowest FBA offer ${money(row.anchorSpread.lowestFbaCents)} · ` +
      `Buy Box ${money(row.anchorSpread.buyBoxCents)} (${row.anchorSpread.pct}% above)`
    );
  }
  if (row.anchorSource === null) return 'No anchor';
  const label = RESEARCH_ANCHOR_SOURCE_LABEL[row.anchorSource];
  return row.anchorPriceCents === null ? label : `${label} ${money(row.anchorPriceCents)}`;
}

/** Reason string -> dedicated copy, from Faraz's brief for the research
 *  page's run strip; any other non-null reason still gets a plain sentence
 *  naming it rather than nothing. */
const RESEARCH_HOLD_SENTENCES: Record<string, string> = {
  incomplete_commitments:
    'Plan on hold: inventory figure is an estimate. Research is complete and shown below.',
};

export function researchHoldSentence(holdReason: string | null): string | null {
  if (holdReason === null) return null;
  return RESEARCH_HOLD_SENTENCES[holdReason] ?? `Plan on hold (${holdReason}).`;
}

// ─────────────────────────────────────────────────────────
// Per-SKU inventory hold (2026-09-10 design doc: docs/plans/
// 2026-09-10-per-sku-inventory-hold-design.md) — publish-run.ts now holds
// only the affected order_plan_lines instead of the whole plan.
// ─────────────────────────────────────────────────────────

/** Per-line badge copy — null for a line that isn't held. Only
 *  'existing_inventory_estimate' exists today (the design doc's only
 *  per-line hold reason); any other future code still gets a plain
 *  fallback rather than nothing. */
export function planLineHoldBadgeLabel(
  line: Pick<OrderPlanLine, 'holdReason' | 'holdDetail'>
): string | null {
  if (line.holdReason === null) return null;
  const units = line.holdDetail?.units ?? null;
  if (line.holdReason === 'existing_inventory_estimate') {
    return `Held: inventory estimate${units !== null ? ` (${units} unit${units === 1 ? '' : 's'} unaccounted)` : ''}`;
  }
  return `Held (${line.holdReason})`;
}

/** Run header copy for the per-line hold count — only meaningful when the
 *  run summary's status is 'published' with heldLines > 0 (see
 *  publishPlanningRun); the plan-wide held sentence (summary.reason) stays
 *  for whole-plan reasons. */
export function planningHeldLinesLabel(heldLines: number): string {
  return `${heldLines} line${heldLines === 1 ? '' : 's'} held`;
}

// ─────────────────────────────────────────────────────────
// Gating verdict (Faraz 2026-09-06, "Amazon is gating human digestible
// categories from us") — the row panel's Listing-gate line and Listing
// section. See packages/schemas/src/planning-run.ts's PlanningGatingStatus/
// PlanningResearchGating doc for the six-state vocabulary this mirrors.
// ─────────────────────────────────────────────────────────

const GATING_STATUS_CHIP: Record<PlanningGatingStatus, { variant: BadgeVariant; label: string }> = {
  open: { variant: 'positive', label: 'Open' },
  approval_required: { variant: 'warning', label: 'Needs approval' },
  not_eligible: { variant: 'negative', label: 'Not eligible' },
  asin_not_found: { variant: 'neutral', label: 'ASIN missing' },
  gated: { variant: 'negative', label: 'Gated' },
  unchecked: { variant: 'neutral', label: 'Not checked yet' },
};

export function gatingChip(status: PlanningGatingStatus): { variant: BadgeVariant; label: string } {
  return GATING_STATUS_CHIP[status];
}

/** Chip `title` — the human-readable reason messages joined by " · " when
 *  Amazon sent any (Faraz 2026-09-06: "store the reason text"), otherwise
 *  the raw reason codes joined by ", " as before. Undefined when there are
 *  neither (an empty title attribute would still render as an empty
 *  tooltip affordance, so this is left unset rather than ""). */
export function gatingReasonsTitle(reasons: string[], messages: string[] = []): string | undefined {
  if (messages.length > 0) return messages.join(' · ');
  return reasons.length > 0 ? reasons.join(', ') : undefined;
}

/** Small tertiary label under the Listing chip — "Brand gate" / "Category
 *  gate" — null for 'unknown' so nothing renders (Faraz 2026-09-06: "we
 *  cannot tell a brand gate from a category gate"; once classified, say
 *  so plainly). */
export function gateKindLabel(gateKind: PlanningGateKind): string | null {
  if (gateKind === 'brand') return 'Brand gate';
  if (gateKind === 'category') return 'Category gate';
  return null;
}

/** "Split the list" (Faraz 2026-09-26) — the Research table row's "Needs
 *  approval: {subject}" line, under the product title. gateKind picks what
 *  the subject names: 'brand' names the gated brand, 'category' the gated
 *  category, else the product itself — falling back to `product` when the
 *  gate-kind-specific field is missing (an older gating row, or Amazon's own
 *  message not carrying it). */
export function approvalNeededSubject(
  gateKind: PlanningGateKind,
  brand: string | null,
  category: string | null,
  product: string
): string {
  if (gateKind === 'brand') return brand ?? product;
  if (gateKind === 'category') return category ?? product;
  return product;
}

// ─────────────────────────────────────────────────────────
// ASIN identity verdict (Faraz 2026-09-06: "I am having issues finding the
// right ASIN. A lot of product links take me to some random auto parts.") —
// the row panel's Listing-match line. See packages/schemas/src/
// planning-run.ts's PlanningResearchIdentity/PlanningResearchAmazon doc for
// the three-state vocabulary this mirrors.
// ─────────────────────────────────────────────────────────

/** The "Wrong product?" chip's/line's `title` tooltip — checkAsinIdentity's
 *  raw reason code translated to a sentence a non-engineer can read. Any
 *  other non-null reason still gets a plain fallback sentence rather than
 *  nothing, same convention as researchHoldSentence above. */
const IDENTITY_MISMATCH_REASON_SENTENCE: Record<string, string> = {
  department_mismatch:
    'Amazon lists this ASIN in a department that does not match the supplier catalogue.',
  no_shared_tokens: 'The Amazon listing title and brand share no words with the supplier item.',
};

export function identityMismatchTitle(reason: string | null): string {
  if (reason && IDENTITY_MISMATCH_REASON_SENTENCE[reason]) {
    return IDENTITY_MISMATCH_REASON_SENTENCE[reason];
  }
  return 'This ASIN may not be the right product.';
}

// ─────────────────────────────────────────────────────────
// Pack size (Faraz 2026-09-07: "A lot of optimals are multipacks. Once we
// calculate the pack correctly they probably drop out of optimal. Why don't
// we look into the Amazon listing title, it says the pack quantity.") —
// the row panel's Verdict-pack line. See packages/schemas/src/
// planning-run.ts's PlanningResearchPack doc for the three-state confidence
// vocabulary this mirrors: 'known' the derived vendorUnitsPerListing is
// trustworthy, 'unclear' Amazon's pack and the supplier's inner pack don't
// divide evenly, 'assumed_single' no Amazon pack evidence at all.
// ─────────────────────────────────────────────────────────

const PACK_SOURCE_LABEL: Record<NonNullable<PlanningResearchPack['source']>, string> = {
  package_quantity: "Amazon's package quantity",
  size: "Amazon's size field",
  title: "Amazon's title",
  // 2026-09-08 pack-override design — candidate-inputs.ts stamps this when
  // a human correction won over the detected pack.
  override: 'a manual override',
};

/** Pack badge for a `known` or `unclear` row — null for `assumed_single`,
 *  which the row panel renders as plain text instead. */
export function packBadge(
  pack: PlanningResearchPack
): { variant: BadgeVariant; label: string; title: string } | null {
  if (pack.confidence === 'known') {
    const sourceLabel = pack.source ? PACK_SOURCE_LABEL[pack.source] : 'Amazon';
    return {
      variant: 'neutral',
      label: `Pack ${pack.vendorUnitsPerListing}`,
      title: `${pack.vendorUnitsPerListing} units per listing, from ${sourceLabel}`,
    };
  }
  if (pack.confidence === 'unclear') {
    return {
      variant: 'warning',
      label: 'Pack unclear',
      title:
        pack.amazonPackQuantity !== null
          ? `Amazon lists a pack of ${pack.amazonPackQuantity}, but that doesn't divide evenly by the supplier's inner pack — units per listing not known.`
          : "Amazon's pack size doesn't divide evenly by the supplier's inner pack — units per listing not known.",
    };
  }
  return null;
}

// ─────────────────────────────────────────────────────────
// Discount buckets (Faraz 2026-09-07: "Ideally we should get 15% from KeHE
// but they only give it when we purchase more. Right now we get 6%. Can we
// add the discount buckets 5, 10, 15 and then show viability?") — the row
// panel's Money-section "buyable at 5/10/15" line. See
// packages/schemas/src/planning-run.ts's PlanningResearchDiscount doc for
// the floorAtPct/targetAtPct/marginPctAt shape this reads.
// ─────────────────────────────────────────────────────────

/** "list" for the 0% bucket, "N%" otherwise — shared by the column label
 *  and its tooltip below. */
function discountBucketLabel(pct: number): string {
  return pct === 0 ? 'list' : `${pct}%`;
}

/** The panel's compact "buyable at" text. `—` when no bucket ever clears
 *  the floor; `target at list` collapses the common case where the row is
 *  already at (or past) target with no discount at all — naming the floor
 *  separately would be redundant once the target already clears. */
export function discountViableLabel(discount: PlanningResearchDiscount): string {
  const { floorAtPct, targetAtPct } = discount;
  if (floorAtPct === null && targetAtPct === null) return '—';
  if (floorAtPct === 0 && targetAtPct === 0) return 'target at list';
  const parts: string[] = [];
  if (floorAtPct !== null) parts.push(`floor ${discountBucketLabel(floorAtPct)}`);
  if (targetAtPct !== null) parts.push(`target ${discountBucketLabel(targetAtPct)}`);
  return parts.length > 0 ? parts.join(' · ') : '—';
}

/** The panel's "buyable at" tooltip — margin % at each non-zero discount
 *  bucket, regardless of whether it clears anything. */
export function discountViableTitle(discount: PlanningResearchDiscount): string {
  const fmt = (pct: number | null) => (pct === null ? 'unknown' : `${pct.toFixed(1)}%`);
  return (
    `Margin at 5% off: ${fmt(discount.marginPctAt['5'])}` +
    ` · 10% off: ${fmt(discount.marginPctAt['10'])}` +
    ` · 15% off: ${fmt(discount.marginPctAt['15'])}`
  );
}

// ─────────────────────────────────────────────────────────
// Velocity floor (Faraz 2026-09-08 design: "velocity floor 15 units a
// month, 8 to 15 marginal") — the row's Sales/mo column chip and the panel's
// Verdict/Demand velocity lines. See packages/schemas/src/planning-run.ts's
// PlanningResearchVelocity doc.
// ─────────────────────────────────────────────────────────

const VELOCITY_TIER_CHIP: Record<PlanningVelocityTier, { variant: BadgeVariant; label: string }> = {
  pass: { variant: 'positive', label: 'Pass' },
  marginal: { variant: 'warning', label: 'Marginal' },
  fail: { variant: 'negative', label: 'Fail' },
  unknown: { variant: 'neutral', label: 'Unknown' },
};

export function velocityChip(tier: PlanningVelocityTier): { variant: BadgeVariant; label: string } {
  return VELOCITY_TIER_CHIP[tier];
}

/** Demand-section source label for the velocity estimate — null covers a
 *  never-probed ASIN, same "say so plainly" convention as gateKindLabel. */
export function velocitySourceLabel(source: PlanningResearchVelocity['source']): string {
  switch (source) {
    case 'badge':
      return "Amazon's monthly-sold badge";
    case 'rank_drops':
      return 'estimated from sales-rank drops';
    case null:
      return 'not probed';
  }
}

// ─────────────────────────────────────────────────────────
// Amazon-retail exclusion (Faraz 2026-09-10 design: "We need to skip or
// mark if Amazon is selling or getting the BB on any item. No way we can
// compete with them.") — the row panel's Verdict "Amazon sells / clear"
// line. See packages/schemas/src/planning-run.ts's
// PlanningAmazonRetailVerdict doc.
// ─────────────────────────────────────────────────────────

const AMAZON_RETAIL_REASON_LABEL: Record<string, string> = {
  amazon_now: 'Amazon has a current offer',
  amazon_recent: 'Amazon sold this in the last 90 days',
  amazon_buybox: 'Amazon holds the Buy Box',
  private_brand: 'Amazon private-label brand',
};

/** The "Amazon" chip's/badge's `title` tooltip — deriveAmazonRetail's raw
 *  reason codes translated to sentences a non-engineer can read, same
 *  convention as identityMismatchTitle above. Falls back to a plain
 *  sentence when a row is flagged with no reasons on file (shouldn't
 *  happen — deriveAmazonRetail always lists at least one — but never
 *  renders an empty tooltip). */
export function amazonRetailReasonsTitle(reasons: string[]): string {
  if (reasons.length === 0) return 'Amazon sells this ASIN.';
  return reasons.map((r) => AMAZON_RETAIL_REASON_LABEL[r] ?? r).join(' · ');
}

/** The row panel's red Amazon-sells chip — null for 'clear'/'unknown'
 *  (nothing renders; the row is not flagged). */
export function amazonRetailChip(amazonRetail: {
  status: 'amazon' | 'clear' | 'unknown';
  reasons: string[];
}): { variant: BadgeVariant; label: string; title: string } | null {
  if (amazonRetail.status !== 'amazon') return null;
  return {
    variant: 'negative',
    label: 'Amazon',
    title: amazonRetailReasonsTitle(amazonRetail.reasons),
  };
}

// ─────────────────────────────────────────────────────────
// Planning-table discount scenarios (Faraz 2026-09-16: "We added the
// discount table because we need the calculations. So assume that we are
// getting these discounts and give me the numbers. At planning, I just
// want to see what discount bucket is feasible. We have to order big to
// get a bigger discount.")
//
// TWO deliberately separate helpers, two deliberately separate price
// bases — a code-review catch (2026-09-16): the Details table's discount
// scenarios and the Planning table's "Discount needed" column must NOT
// share a computation, because they answer different questions from
// different prices:
//
//   - planLineDiscountScenarios (Details table only) is priced at
//     research.marketCheck.marketPriceCents — the live Amazon price this
//     run happened to see. The server only fills marketCheck from a
//     supplier price-file match; with no match every cell used to read
//     "Unknown" even though price/fee/prep/units were all known and a
//     plain unit-cost basis (line.expectedUnitCostCents) was sitting
//     right there. Mirrors buying-math.ts's supplierCostScenarios/
//     calculateBuyingEconomics with zero freight.
//
//   - planLineDiscountNeeded (Planning table's "Discount needed" column)
//     is priced at research.anchorPriceCents/costCents/estMarginCents —
//     the EXACT SAME anchor inputs the row's own Estimated profit/%
//     cells already show (research.estMarginCents/marginPct). Mirrors
//     research-projection.ts's computeRowDiscount exactly (same formula,
//     same bucket order, same null short-circuit), so the column can
//     never disagree with the row it sits in, or with the Research
//     page's own discount column. anchorPriceCents and marketPriceCents
//     are frequently different numbers (Buy Box vs. this run's live
//     price probe) — using the wrong one produced a real bug: a row
//     could show "$3.10 · 20.7%" profit next to "Not viable at 15% off",
//     because the column was market-priced while the row was
//     anchor-priced.
// ─────────────────────────────────────────────────────────

/** Every bucket both helpers show, list included — same set
 *  buying-math.ts's supplierCostScenarios and computeRowDiscount use. */
const PLAN_LINE_DISCOUNT_PCT_BUCKETS = [0, 5, 10, 15] as const;

export interface PlanLineDiscountScenario {
  discountPct: number;
  /** basisCostCents scaled by (100 - discountPct)/100, rounded — null when
   *  no cost basis (supplier price file or your own unit cost) is known. */
  vendorUnitCostCents: number | null;
  /** price − fee − prep − vendorUnitCostCents × unitsPerListing — the same
   *  zero-freight, one-listing case as calculateBuyingEconomics. Null if
   *  any input (including a missing marketCheck) is null. */
  profitPerListingCents: number | null;
  /** profitPerListingCents / marketPriceCents × 100 — null when profit is
   *  unknown or the price isn't a positive number. */
  marginPct: number | null;
}

export interface PlanLineDiscountScenarios {
  /** True exactly when bucket 0's profit is a real number — i.e. a cost
   *  basis, a market check, and its fee/prep are all known. Every other
   *  bucket's nullability tracks bucket 0's (same basis, same
   *  price/fee/prep). Details-table-only — has no viability/threshold
   *  concept of its own; see planLineDiscountNeeded for that. */
  computable: boolean;
  /** Which unit cost fed vendorUnitCostCents — 'supplier_list' when a
   *  supplier price-file match is on file, 'your_cost' when falling back
   *  to the line's own expectedUnitCostCents, null when neither exists. */
  basis: 'supplier_list' | 'your_cost' | null;
  /** One entry per PLAN_LINE_DISCOUNT_PCT_BUCKETS bucket, in order. */
  scenarios: PlanLineDiscountScenario[];
}

/**
 * planLineDiscountScenarios(line) — the Details table's market-priced
 * scenario numbers ONLY. Pure and total: every input combination returns
 * a full 4-bucket `scenarios` array (with per-field nulls where unknown),
 * never throws. Do not use this for any "is this discount viable" claim —
 * see planLineDiscountNeeded for that (anchor-priced, matches the row).
 */
export function planLineDiscountScenarios(
  line: Pick<OrderPlanLine, 'research' | 'expectedUnitCostCents'>
): PlanLineDiscountScenarios {
  const r = line.research;
  const supplierListCostCents = r?.supplierListSource?.listUnitCostCents ?? null;
  const basisCostCents = supplierListCostCents ?? line.expectedUnitCostCents ?? null;
  const basis: PlanLineDiscountScenarios['basis'] =
    supplierListCostCents !== null
      ? 'supplier_list'
      : line.expectedUnitCostCents !== null
        ? 'your_cost'
        : null;
  const check = r?.marketCheck ?? null;

  const scenarios: PlanLineDiscountScenario[] = PLAN_LINE_DISCOUNT_PCT_BUCKETS.map(
    (discountPct) => {
      const vendorUnitCostCents =
        basisCostCents === null ? null : Math.round((basisCostCents * (100 - discountPct)) / 100);
      const profitPerListingCents =
        check === null ||
        vendorUnitCostCents === null ||
        check.liveFeePerListingCents === null ||
        check.prepPerListingCents === null
          ? null
          : check.marketPriceCents -
            check.liveFeePerListingCents -
            check.prepPerListingCents -
            vendorUnitCostCents * check.unitsPerListing;
      const marginPct =
        profitPerListingCents === null || check === null || check.marketPriceCents <= 0
          ? null
          : (profitPerListingCents / check.marketPriceCents) * 100;
      return { discountPct, vendorUnitCostCents, profitPerListingCents, marginPct };
    }
  );

  // Bucket 0's inputs (basis, marketCheck, its fee/prep) determine every
  // other bucket's nullability too — a single flag for "do we have real
  // numbers at all."
  const computable = scenarios[0]!.profitPerListingCents !== null;

  return { computable, basis, scenarios };
}

export interface PlanLineDiscountNeeded {
  /** True only when the row has a real anchor price, listing cost,
   *  margin, AND a known floor/target threshold to compare against —
   *  mirrors computeRowDiscount's own null short-circuit, PLUS a
   *  thresholds check: a missing threshold means "we don't know," never
   *  "not viable" (a code-review catch — the column used to say "Not
   *  viable at 15% off" for a row whose thresholds simply hadn't loaded
   *  yet). */
  computable: boolean;
  /** Same shape research-projection.ts's computeRowDiscount produces,
   *  computed from the row's own anchorPriceCents/costCents/estMarginCents
   *  and thresholds — the exact inputs the row's Estimated profit/% cells
   *  already show. Never market-check-priced; see planLineDiscountScenarios
   *  (Details table only) for that. */
  discount: PlanningResearchDiscount;
}

/**
 * planLineDiscountNeeded(line) — the Planning table's "Discount needed"
 * column. A line-for-line port of research-projection.ts's
 * computeRowDiscount (same formula, same bucket order, same null
 * short-circuit) plus the thresholds-known check described above —
 * api code isn't importable into the web bundle, so this is hand-verified
 * against that function rather than re-derived from scratch; see this
 * module's test file for a fixture whose numbers are checked against
 * computeRowDiscount's own math by hand.
 */
export function planLineDiscountNeeded(
  line: Pick<OrderPlanLine, 'research'>
): PlanLineDiscountNeeded {
  const r = line.research;
  const anchorPriceCents = r?.anchorPriceCents ?? null;
  const costCents = r?.costCents ?? null;
  const estMarginCents = r?.estMarginCents ?? null;
  const thresholds = r?.thresholds ?? null;

  const marginPctAt: PlanningResearchDiscount['marginPctAt'] = {
    '5': null,
    '10': null,
    '15': null,
  };

  if (
    anchorPriceCents === null ||
    anchorPriceCents <= 0 ||
    costCents === null ||
    estMarginCents === null ||
    thresholds === null
  ) {
    return { computable: false, discount: { floorAtPct: null, targetAtPct: null, marginPctAt } };
  }

  const fracAtBucket = PLAN_LINE_DISCOUNT_PCT_BUCKETS.map((discountPct) => ({
    discountPct,
    marginFrac: (estMarginCents + (costCents * discountPct) / 100) / anchorPriceCents,
  }));
  for (const { discountPct, marginFrac } of fracAtBucket) {
    if (discountPct > 0) marginPctAt[String(discountPct) as '5' | '10' | '15'] = marginFrac * 100;
  }
  const floorAtPct =
    fracAtBucket.find((b) => b.marginFrac >= thresholds.minFrac)?.discountPct ?? null;
  const targetAtPct =
    fracAtBucket.find((b) => b.marginFrac >= thresholds.targetFrac)?.discountPct ?? null;

  return { computable: true, discount: { floorAtPct, targetAtPct, marginPctAt } };
}

/**
 * Planning table's "Discount needed" column label — distinct from the
 * Research page's discountViableLabel (which has no "not computable" case
 * of its own; the research pipeline always has a real anchor/margin by the
 * time a row is shown there). Reuses discountViableLabel's exact wording
 * for the viable case rather than re-deriving it. Takes planLineDiscountNeeded's
 * output — never planLineDiscountScenarios's (see module doc above).
 */
export function planLineDiscountNeededLabel(discount: PlanLineDiscountNeeded): string {
  if (!discount.computable) return '—';
  if (discount.discount.floorAtPct === null) return 'Not viable at 15% off';
  return discountViableLabel(discount.discount);
}

/**
 * Per-row Amazon/Keepa evidence status (2026-09-11 research-verdict-
 * simplification design; used by both ResearchPage's table row and, since
 * the 2026-09-17 provider-queues Task 6 UI, ResearchRowPanel's detail view
 * — moved here so neither page module has to import the other for it).
 * Keep the status copy honest: Amazon is the baseline market check, while
 * Keepa is shown only when this row carries one of its enrichment fields.
 */
export function researchStatus(row: PlanningResearchRow): {
  amazon: 'complete' | 'pending';
  keepa: 'complete' | 'pending';
} {
  return {
    amazon: row.anchorPriceCents !== null || row.estFeesCents !== null ? 'complete' : 'pending',
    keepa:
      row.priceContext.bbAvg90Cents !== null ||
      row.priceContext.bbAvg30Cents !== null ||
      row.velocity.source === 'rank_drops'
        ? 'complete'
        : 'pending',
  };
}
